/**
 * Looks up a translation string by key from window.theme.strings.
 * Falls back to the provided fallback or the key itself if not found.
 *
 * @param {string} key - Dot-separated translation key
 * @param {string} [fallback] - Optional fallback string
 * @returns {string} - The translated string or fallback
 */
function t(key, fallback) {
  // Default fallback if none provided
  const defaultText = fallback || key;

  // Check if theme and strings exist first
  if (!window.theme || !window.theme.strings) {
    return defaultText;
  }

  // Try to find the translation by navigating the nested structure
  try {
    const parts = key.split('.');
    // Handle the new structure
    let result = window.theme.strings;

    // Navigate through each part of the key
    for (const part of parts) {
      if (result[part] === undefined) return defaultText;
      result = result[part];
    }

    return result || defaultText;
  } catch (e) {
    return defaultText;
  }
}

// ---------------
// Global Debug Configuration
// ---------------
// Logging flag and helper - moved to global scope for use in all components
let ENABLE_LOGS = false; // Set to true to enable logs

// ---------------
// PDP Custom Dropdown Code
// ---------------
document.addEventListener('DOMContentLoaded', () => {
  // Get current market from <html data-market="...">
  const market = document.documentElement.getAttribute('data-market');

  // 100x140 frames need special kerbside freight, so they are only offered in the
  // markets allowed under Theme settings > Frames (<html data-oversized-frames="...">).
  const oversizedFramesAllowed = document.documentElement.dataset.oversizedFrames === 'true';
  
  // Translation helper function - improved with fallbacks
  /**
   * Logs debug information to the console with the [PEL] prefix if ENABLE_LOGS is true.
   * @param {...any} args
   */
  function log(...args) {
    if (ENABLE_LOGS) console.log('[PEL]', ...args);
  }

  log('DOMContentLoaded fired');
  log('Current market:', market);

  // ---------------
  // Price State Management
  // ---------------
  // Centralized price state management
  window.priceState = {
    variantPrice: 0,
    variantComparePrice: 0,
    framePrice: 0,
    frameComparePrice: 0,
    /**
     * Computes the total price by summing variant and frame prices.
     * @returns {number}
     */
    get totalPrice() {
      return parseFloat(this.variantPrice) + parseFloat(this.framePrice || 0);
    },
    /**
     * Computes the total compare-at price (if available) for variant and frame.
     * @returns {number|null}
     */
    get totalComparePrice() {
      return (
        this.variantComparePrice &&
        parseFloat(this.variantComparePrice) + parseFloat(this.frameComparePrice || 0)
      );
    },
    /**
     * Triggers a UI update for prices using the current state.
     * Side effect: calls global window.updatePrices.
     */
    updatePrices() {
      window.updatePrices(
        this.variantPrice,
        this.framePrice,
        this.variantComparePrice,
        this.frameComparePrice
      );
    },
  };

  // ---------------
  // Frame Selection Management
  // ---------------
  // Enhanced frame selection object with methods to update prices
  window.frameSelection = {
    isFrameSelected: false,
    framePrice: 0,
    frameHandle: null,
    frameTitle: null,
    frameVariant: null,
    frameId: null,
    frameComparePrice: 0,

    // Add methods to update the object and trigger price updates

    /**
     * Selects a frame and updates all related state.
     * @param {Object} data - Frame data (price, comparePrice, handle, title, variant, id)
     * @returns {this}
     * Side effects: Updates global price state and triggers price update.
     */
    setFrame(data) {
      // Ensure data is valid
      if (!data || typeof data !== 'object') {
        console.error('[PEL] Invalid frame data provided to setFrame():', data);
        return;
      }

      // Update all properties
      this.isFrameSelected = true;
      this.framePrice = parseFloat(data.price || 0);
      this.frameComparePrice = parseFloat(data.comparePrice || 0);
      this.frameHandle = data.handle || null;
      this.frameTitle = data.title || null;
      this.frameVariant = data.variant || null;
      this.frameId = data.id || null;

      // Update global price state using sync function if available
      if (typeof window.syncFrameState === 'function') {
        window.syncFrameState();
      } else {
        // Fallback direct update
        window.priceState.framePrice = this.framePrice;
        window.priceState.frameComparePrice = this.frameComparePrice;
      }

      // Log current state
      if (ENABLE_LOGS) {
        console.log('[PEL] Frame selected:', {
          price: this.framePrice,
          comparePrice: this.frameComparePrice,
          title: this.frameTitle,
          priceState: window.priceState,
        });
      }

      // Trigger price update
      window.priceState.updatePrices();

      return this; // Enable chaining
    },

    /**
     * Clears the current frame selection and resets related state.
     * Side effects: Updates global price state and triggers price update.
     */
    clearFrame() {
      // Reset all properties
      this.isFrameSelected = false;
      this.framePrice = 0;
      this.frameComparePrice = 0;
      this.frameHandle = null;
      this.frameTitle = null;
      this.frameVariant = null;
      this.frameId = null;

      // Update global price state using sync function if available
      if (typeof window.syncFrameState === 'function') {
        window.syncFrameState();
      } else {
        // Fallback direct update
        window.priceState.framePrice = 0;
        window.priceState.frameComparePrice = 0;
      }

      // Show dynamic delivery calculation when frame is cleared
      if (typeof window.updateDeliveryEstimate === 'function') {
        window.updateDeliveryEstimate(false);
      }

      // Log current state
      if (ENABLE_LOGS) {
        console.log('[PEL] Frame cleared, price state:', window.priceState);
      }

      // Trigger price update
      window.priceState.updatePrices();

      return this; // Enable chaining
    },
  };

  // ---------------
  // State Synchronization
  // ---------------
  // Add synchronization method to ensure frameSelection and priceState stay aligned
  /**
   * Synchronizes frameSelection and priceState, ensuring both are aligned with the current selection.
   * Side effects: Mutates window.priceState.
   */
  function syncFrameState() {
    // If a frame is selected, ensure priceState has its values
    if (window.frameSelection.isFrameSelected) {
      window.priceState.framePrice = window.frameSelection.framePrice;
      window.priceState.frameComparePrice = window.frameSelection.frameComparePrice;
    } else {
      // No frame selected, ensure frame prices are 0
      window.priceState.framePrice = 0;
      window.priceState.frameComparePrice = 0;
    }
    return window.priceState;
  }

  // Track the latest variant ID to handle rapid switching
  window.latestVariantId = null;

  // Listen for variant changes from template-product.js
  document.addEventListener('variant:changed', event => {
    const variantId = event.detail.variantId;

    log('variant:changed event received for variant ID', variantId, {
      variantPrice: window.priceState.variantPrice,
      framePrice: window.priceState.framePrice,
      frameSelected: window.frameSelection.isFrameSelected,
      totalPrice: window.priceState.totalPrice,
    });
    
    // IMPORTANT CHANGE: Always accept variant:changed events
    // Instead of skipping events, always use the most recent one
    // This ensures synchronization between tracking mechanisms
    
    // Update the latest variant ID
    window.latestVariantId = variantId;

    // Update the price state with new variant info
    window.priceState.variantPrice = event.detail.price;
    window.priceState.variantComparePrice = event.detail.comparePrice;

    // Explicitly sync frame data from frameSelection to priceState
    syncFrameState();

    // Update frame dropdown display if frame is selected (this may update frame prices)
    if (window.frameSelection.isFrameSelected && event.detail.variantTitle) {
      updateFrameDropdownDisplay(event.detail.variantTitle);
    } else {
      // If no frame selected, still update dropdown options for correct prices/availability
      if (event.detail.variantTitle) {
        updateAllDropdownOptions(event.detail.variantTitle);
      }
      // Then trigger price update
      window.priceState.updatePrices();
    }

    // Debug logging to track state
    log('Processing variant:changed event for variant ID', variantId, {
      variantPrice: window.priceState.variantPrice,
      framePrice: window.priceState.framePrice,
      frameSelected: window.frameSelection.isFrameSelected,
      totalPrice: window.priceState.totalPrice,
    });
  });

  // Make syncFrameState available globally for debugging
  window.syncFrameState = syncFrameState;

  // ---------------
  // Debug Helpers
  // ---------------
  // Add debug helper function
  /**
   * Debug helper to log the current price and frame selection state to the console.
   */
  window.debugPriceState = function () {
    console.group('Price State Debug');
    console.log('frameSelection:', {
      ...window.frameSelection,
    });
    console.log('priceState:', {
      ...window.priceState,
      framePrice: window.priceState.framePrice,
      variantComparePrice: window.priceState.variantComparePrice,
      frameComparePrice: window.priceState.frameComparePrice,
      totalPrice: window.priceState.totalPrice,
      totalComparePrice: window.priceState.totalComparePrice,
    });

    // Check for synchronization issues
    const issuesFound = [];
    if (window.frameSelection.isFrameSelected && window.priceState.framePrice === 0) {
      issuesFound.push('Frame is selected but priceState.framePrice is 0');
    }
    if (
      window.frameSelection.isFrameSelected &&
      window.priceState.framePrice !== window.frameSelection.framePrice
    ) {
      issuesFound.push("Frame prices don't match between objects");
    }
    if (!window.frameSelection.isFrameSelected && window.priceState.framePrice > 0) {
      issuesFound.push('No frame selected but priceState.framePrice > 0');
    }

    if (issuesFound.length > 0) {
      console.warn('Synchronization issues found:', issuesFound);
      console.log('Attempting to fix...');
      window.syncFrameState();
      console.log('After sync:', {
        framePrice: window.priceState.framePrice,
        totalPrice: window.priceState.totalPrice,
      });
    } else {
      console.log('No synchronization issues found');
    }

    console.groupEnd();
  };

  // Run initial sync to ensure state is consistent on load
  syncFrameState();

  // ---------------
  // Price Update Logic
  // ---------------
  // Universal price update function with debounce mechanism
  let priceUpdateTimer = null;
  /**
   * Updates all price displays throughout the product page.
   * Applies debouncing to avoid redundant updates from rapid changes.
   *
   * @param {Number} price - The variant price
   * @param {Number} framePrice - The optional frame price
   * @param {Number|null} comparePrice - The variant compare-at price (optional)
   * @param {Number} frameComparePrice - The frame compare price (optional)
   * Side effects: Updates DOM elements showing price, compare price, and custom button price.
   */
  function updatePrices(price, framePrice = 0, comparePrice = null, frameComparePrice = 0) {
    log('updatePrices called with', { price, framePrice, comparePrice, frameComparePrice });

    // Clear any pending updates
    if (priceUpdateTimer) {
      clearTimeout(priceUpdateTimer);
    }

    // Create a short delay to ensure we get the most recent update when switching rapidly
    priceUpdateTimer = setTimeout(() => {
      // Use parameters if provided, otherwise fall back to the global state
      // This helps with backward compatibility where old code may call this directly
      const effectivePrice = price || window.priceState.variantPrice;
      const effectiveFramePrice =
        framePrice !== undefined ? framePrice : window.priceState.framePrice;
      const effectiveComparePrice = comparePrice || window.priceState.variantComparePrice;
      const effectiveFrameComparePrice =
        frameComparePrice !== undefined ? frameComparePrice : window.priceState.frameComparePrice;

      log('Effective prices:', {
        effectivePrice,
        effectiveFramePrice,
        effectiveComparePrice,
        effectiveFrameComparePrice,
      });

      const totalPrice = effectiveFramePrice
        ? parseFloat(effectivePrice) + parseFloat(effectiveFramePrice)
        : parseFloat(effectivePrice);
      const totalComparePrice =
        effectiveComparePrice &&
        parseFloat(effectiveComparePrice) + parseFloat(effectiveFrameComparePrice || 0);

      // Main add to cart button price
      if (mainAtc) {
        const mainPriceContainer = mainAtc.querySelector('.total-price');
        if (mainPriceContainer) {
          mainPriceContainer.textContent = Woolman.Utils.formatMoney(
            totalPrice,
            theme.settings.moneyFormat
          );
          log('Updated mainAtc price to', totalPrice);
        }

        // Main compare price (if exists)
        const mainComparePriceContainer = mainAtc.querySelector('.total-compare-price');
        if (mainComparePriceContainer && totalComparePrice) {
          mainComparePriceContainer.textContent = Woolman.Utils.formatMoney(
            totalComparePrice,
            theme.settings.moneyFormat
          );
          mainComparePriceContainer.style.display = 'inline-block';
          log('Updated mainAtc compare price to', totalComparePrice);
        } else if (mainComparePriceContainer) {
          mainComparePriceContainer.style.display = 'none';
        }
      }

      
      // Main product price - use totalPrice for correct calculation
      if (mainPrdPrice) {
        // Make sure we're using the total price (variant + frame)
        mainPrdPrice.textContent = Woolman.Utils.formatMoney(
          totalPrice,
          theme.settings.moneyFormat
        );
        log('Updated mainPrdPrice to', totalPrice, '(variant:', price, '+ frame:', framePrice, ')');

        // Main product compare price
        const mainPrdComparePrice = document.querySelector('.product-main .price__compare .price-item');
        if (mainPrdComparePrice && totalComparePrice) {
          mainPrdComparePrice.textContent = Woolman.Utils.formatMoney(
            totalComparePrice,
            theme.settings.moneyFormat
          );
          mainPrdComparePrice.closest('.price__compare').style.display = 'block';
          log('Updated mainPrdComparePrice to', totalComparePrice);
        } else if (mainPrdComparePrice) {
          mainPrdComparePrice.closest('.price__compare').style.display = 'none';
        }

        // Additional compare price elements (for sale items)
        // Skip elements inside .variant-option — those have their own per-variant compare prices
        const comparePriceElements = document.querySelectorAll('.product-main .price-item--regular');
        comparePriceElements.forEach(element => {
          if (element.closest('.variant-option')) return;
          if (totalComparePrice) {
            element.textContent = Woolman.Utils.formatMoney(
              totalComparePrice,
              theme.settings.moneyFormat
            );
            element.style.display = 'inline';
            log('Updated compare price element to', totalComparePrice);
          }
        });
      }

      // Sale price - update if it exists
      const salePriceElement = document.querySelector('.product-main .price-item--sale');
      if (salePriceElement) {
        salePriceElement.textContent = Woolman.Utils.formatMoney(
          totalPrice,
          theme.settings.moneyFormat
        );
        log('Updated sale price to', totalPrice);
      }

      // Regular price - update if it exists (for products on sale)
      const regularPriceElement = document.querySelector('.product-main .price-item--regular:not(.variant-option .price-item--regular)');
      if (regularPriceElement && totalComparePrice) {
        regularPriceElement.textContent = Woolman.Utils.formatMoney(
          totalComparePrice,
          theme.settings.moneyFormat
        );
        log('Updated regular price to', totalComparePrice);
      }

      // Buy bar price
      if (buyBarPrice) {
        buyBarPrice.textContent = Woolman.Utils.formatMoney(totalPrice, theme.settings.moneyFormat);
        log('Updated buyBarPrice to', totalPrice);

        // Buy bar compare price
        const buyBarComparePrice = document.querySelector('.the-buy-bar .compare-price');
        if (buyBarComparePrice && totalComparePrice) {
          buyBarComparePrice.textContent = Woolman.Utils.formatMoney(
            totalComparePrice,
            theme.settings.moneyFormat
          );
          buyBarComparePrice.style.display = 'inline-block';
          log('Updated buyBarComparePrice to', totalComparePrice);
        } else if (buyBarComparePrice) {
          buyBarComparePrice.style.display = 'none';
        }
      }

      // Size price tag (if it exists)
      const sizePrice = document.querySelector('.size-total-price');
      if (sizePrice) {
        const variantPriceOnly = price || window.priceState.variantPrice || 0;
        sizePrice.innerHTML = ` + ${Woolman.Utils.formatMoney(
          variantPriceOnly,
          theme.settings.moneyFormat
        )}`;
        log('Updated sizePrice to', variantPriceOnly);
      }

      // Custom button price (for frames)
      if (customBtn) {
        const customPriceContainer = customBtn.querySelector('.total-price');
        if (customPriceContainer) {
          customPriceContainer.textContent = Woolman.Utils.formatMoney(
            totalPrice,
            theme.settings.moneyFormat
          );
          log('Updated custom button price to', totalPrice);
        }

        // Custom button compare price
        if (comparePriceContainer && totalComparePrice) {
          comparePriceContainer.textContent = Woolman.Utils.formatMoney(
            totalComparePrice,
            theme.settings.moneyFormat
          );
          comparePriceContainer.style.display = 'inline-block';
          log('Updated custom button compare price to', totalComparePrice);
        } else if (comparePriceContainer) {
          comparePriceContainer.style.display = 'none';
        }
      }

      // Buy button bar button-check-option price
      const buyBarCustomBtn = document.querySelector('.the-buy-bar #button-check-option');
      if (buyBarCustomBtn) {
        const buyBarCustomPriceContainer = buyBarCustomBtn.querySelector('.total-price');
        if (buyBarCustomPriceContainer) {
          // Check if tiered pricing is enabled and get the discounted price
          const buyBarTierComponent = document.querySelector('.the-buy-bar tier-discounts-price');
          let finalVariantPrice = effectivePrice;
          
          if (buyBarTierComponent && typeof buyBarTierComponent.calculateDiscount === 'function') {
            // Use the tiered pricing calculation
            const discountInfo = buyBarTierComponent.calculateDiscount(effectivePrice);
            finalVariantPrice = discountInfo.finalPrice || effectivePrice;
            log('Using tiered price for buy bar button:', finalVariantPrice);
          }
          
          const totalPriceWithTierDiscount = effectiveFramePrice
            ? finalVariantPrice + parseFloat(effectiveFramePrice)
            : finalVariantPrice;
          
          buyBarCustomPriceContainer.textContent = Woolman.Utils.formatMoney(
            totalPriceWithTierDiscount,
            theme.settings.moneyFormat
          );
          log('Updated buy bar custom button price to', totalPriceWithTierDiscount);
        }

        // Buy button bar custom button compare price
        const buyBarCustomComparePriceContainer = buyBarCustomBtn.querySelector('.total-compare-price');
        if (buyBarCustomComparePriceContainer && totalComparePrice) {
          buyBarCustomComparePriceContainer.textContent = Woolman.Utils.formatMoney(
            totalComparePrice,
            theme.settings.moneyFormat
          );
          buyBarCustomComparePriceContainer.style.display = 'inline';
          buyBarCustomComparePriceContainer.style.textDecoration = 'line-through';
          buyBarCustomComparePriceContainer.style.marginLeft = '8px';
          buyBarCustomComparePriceContainer.style.opacity = '0.7';
          log('Updated buy bar custom button compare price to', totalComparePrice);
        } else if (buyBarCustomComparePriceContainer) {
          buyBarCustomComparePriceContainer.style.display = 'none';
        }
      }

      // Loop over all product-price-container elements and update prices
      const productPriceContainers = document.querySelectorAll('.product-price-container');
      productPriceContainers.forEach(container => {
        // Update main price elements within each container
        const priceElements = container.querySelectorAll('.price-item');
        priceElements.forEach(element => {
          // Skip compare price elements (they have specific classes)
          if (element.classList.contains('price-item--compare') || 
              element.classList.contains('price-item--regular') ||
              element.closest('.price__compare')) {
            return;
          }
          
          // Update the price
          element.textContent = Woolman.Utils.formatMoney(
            totalPrice,
            theme.settings.moneyFormat
          );
          log('Updated product-price-container price element to', totalPrice);
        });

        // Update compare price elements within each container
        // Skip elements inside .variant-option — those have their own per-variant compare prices
        const comparePriceElements = container.querySelectorAll('.price-item--compare, .price-item--regular');
        comparePriceElements.forEach(element => {
          if (element.closest('.variant-option')) return;
          if (totalComparePrice) {
            element.textContent = Woolman.Utils.formatMoney(
              totalComparePrice,
              theme.settings.moneyFormat
            );
            element.style.display = 'inline';
            log('Updated product-price-container compare price element to', totalComparePrice);
          }
        });

        // Update any price__compare containers
        const priceCompareContainers = container.querySelectorAll('.price__compare');
        priceCompareContainers.forEach(compareContainer => {
          if (totalComparePrice) {
            compareContainer.style.display = 'block';
            const comparePriceItem = compareContainer.querySelector('.price-item');
            if (comparePriceItem) {
              comparePriceItem.textContent = Woolman.Utils.formatMoney(
                totalComparePrice,
                theme.settings.moneyFormat
              );
              log('Updated product-price-container price__compare to', totalComparePrice);
            }
          } else {
            compareContainer.style.display = 'none';
          }
        });
      });
    }, 10); // Small timeout to ensure we process the latest change
  }
  window.updatePrices = updatePrices;

  const dropdownContainer = document.querySelector('.dropdown-container');
  const selectedOption = document.querySelector('.selected-option');
  const availableText = document.querySelector('.available-text');
  const dropdownList = document.querySelector('.dropdwon-list');
  const dropdownOptions = document.querySelectorAll('.dropdown-option');
  const customBtn = document.querySelector('.product-form__buttons .custom-btn');
  const mainPrdPrice = document.querySelector('.price__regular .price-item');
  const buyBarPrice = document.querySelector('.the-buy-bar .price-item');
  const variantSelect = document.querySelector('variant-selects select');
  const estimateDelivery = document.querySelector('.estimate-shipping .normal-estimate');
  const extendedDelivery = document.querySelector('.estimate-shipping .extended');
  let priceContainer, comparePriceContainer;
  const mainAtc = document.querySelector('.product-form__submit');

  // Parse frame-data JSON
  let frameData = {};
  const frameDataScript = document.getElementById('frame-data');
  if (frameDataScript) {
    try {
      frameData = JSON.parse(frameDataScript.textContent);
      window.frameData = frameData;
      log('Frame data parsed successfully', Object.keys(frameData));
    } catch (e) {
      console.error('[PEL] Failed to parse frame-data', e);
    }
  }

  // Simple API cache
  const apiCache = {};
  /**
   * Fetches a URL and caches the response for subsequent calls.
   * Used to avoid redundant API requests for the same resource.
   *
   * @param {string} url
   * @returns {Promise<Response>}
   */
  async function cachedFetch(url) {
    if (apiCache[url]) {
      return Promise.resolve(apiCache[url]);
    }
    const response = await fetch(url);
    const data = await response.json();
    apiCache[url] = data;
    return data;
  }

  // ---------------
  // Helper Functions
  // ---------------
  /**
   * Gets the currently selected variant value from either button, dropdown, or drawer picker
   * @returns {string|null} The selected variant value or null if none found
   */
  function getCurrentVariantValue() {
    // Try drawer picker first
    const drawerElement = document.querySelector('variant-drawer');
    if (drawerElement && drawerElement.currentVariant) {
      // Get the size from the current variant (usually the first option)
      return drawerElement.currentVariant.option1 || null;
    }
    
    // Try selectedVariantText from drawer (fallback)
    const selectedVariantText = document.querySelector('#selectedVariantText');
    if (selectedVariantText && selectedVariantText.dataset.value) {
      return selectedVariantText.dataset.value;
    }
    if (selectedVariantText && selectedVariantText.textContent) {
      const text = selectedVariantText.textContent.trim();
      // Extract size from formatted text like "A4 (21 x 30 cm)" -> "A4"
      const match = text.match(/^([A-Z0-9]+)/);
      if (match) return match[1];
    }
    
    // Try button picker
    const radioInput = document.querySelector(".product-variant-options input[type='radio']:checked");
    if (radioInput) {
      return radioInput.value;
    }
    
    // Try dropdown picker
    const selectInput = document.querySelector('variant-selects select');
    if (selectInput) {
      return selectInput.value;
    }
    
    return null;
  }

  /**
   * Gets the product handle from either button or dropdown picker
   * @returns {string|null} The product handle or null if none found
   */
  function getCurrentProductHandle() {
    const variantRadios = document.querySelector('variant-radios');
    const variantSelects = document.querySelector('variant-selects');
    
    return variantRadios 
      ? variantRadios.getAttribute('data-handle')
      : variantSelects?.getAttribute('data-handle');
  }

  // ---------------
  // Frame Dropdown Display Update
  // ---------------
  /**
   * Resets frame selection to "No Frame" state
   */
  function resetToNoFrame() {
    const selectedOption = document.querySelector('.selected-option');
    const customBtn = document.querySelector('.custom-btn');
    const mainAtc = document.querySelector('.product-form__submit');
    
    if (!selectedOption) return;
    
    // Update visual state
    selectedOption.classList.add('none');
    selectedOption.classList.add('none-selected');
    selectedOption.setAttribute('data-handle', 'none');
    selectedOption.removeAttribute('data-title');
    selectedOption.removeAttribute('data-variant');
    
    // Hide custom button, show main ATC
    if (customBtn) customBtn.style.display = 'none';
    if (mainAtc) mainAtc.style.display = 'inline-flex';
    
    // Show dynamic delivery calculation when frame is cleared
    if (typeof window.updateDeliveryEstimate === 'function') {
      window.updateDeliveryEstimate(false);
    }
    
    // Update frame selection state
    window.frameSelection.clearFrame();
    
    // Update display
    selectedOption.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 60 60" fill="none">
        <rect width="60" height="60" fill="#F1F1F1"/>
        <path d="M59 1L1 59" stroke="white" stroke-width="2" stroke-linecap="round"/>
      </svg>
      <div class="option--details">
        <span class="option-title">${t('product.frame_picker.no_frame', 'No Frame')}
        </span>
        <div class="option-frame-detail">
          <span class="glass-type">${t(
            'product.frame_picker.frame_own_print',
            'You will frame your own print'
          )}
          </span>
        </div>
      </div>`;
    
    log('Frame selection reset to No Frame');
  }

  /**
   * Updates all dropdown options with new variant prices and availability
   * @param {string} newVariantTitle - The new variant title (e.g., "100x140", "A4")
   */
  function updateAllDropdownOptions(newVariantTitle) {
    const dropdownOptions = document.querySelectorAll('.dropdown-option');
    
    dropdownOptions.forEach(option => {
      const optionHandle = option.getAttribute('data-handle');
      if (!optionHandle || optionHandle === 'none') return; // Skip "No Frame" option
      
      const optionVariant = option.querySelector('.selected-frame-variant');
      const optionSelectGlass = option.querySelector('.selected-glass');
      const optionPrice = option.querySelector('.frame-price');
      
      if (!optionVariant || !optionPrice) return;
      
      // Use frameData for frame product
      let frameProduct = frameData[optionHandle];
      if (frameProduct) {
        let matchedVariant = frameProduct.variants.find(
          variant => variant.title === newVariantTitle
        );
        
        // Hide 100x140 unless this market allows oversized frames
        if (matchedVariant && matchedVariant.title === '100x140' && !oversizedFramesAllowed) {
          option.style.display = 'none';
          return;
        }
        
        if (!matchedVariant) {
          option.style.display = 'none';
          return;
        }
        
        // Format the variant title
        let updatedMatchedVariantTitle;
        updatedMatchedVariantTitle = window.peletonSizeLabel(matchedVariant.title, 'short');
        
        // Get glass type
        let variantMetaValue;
        document.querySelectorAll('.variant-meta-content').forEach(variantMeta => {
          let variantMetaTitle = variantMeta.getAttribute('data-title');
          if (matchedVariant.title == variantMetaTitle) {
            variantMetaValue = variantMeta.getAttribute('data-glass');
          }
        });
        
        // Update option display
        option.style.display = 'flex';
        optionSelectGlass.textContent = variantMetaValue;
        optionVariant.textContent = updatedMatchedVariantTitle;
        optionVariant.dataset.size = matchedVariant.title;
        option.setAttribute('value', matchedVariant.id);
        option.setAttribute('data-price', matchedVariant.price);
        option.setAttribute('data-compare', matchedVariant.compare_at_price);
        optionPrice.textContent = Woolman.Utils.formatMoney(
          matchedVariant.price,
          theme.settings.moneyFormat
        );
        
        log('Updated dropdown option for', optionHandle, 'with variant', newVariantTitle);
      }
    });
  }

  /**
   * Updates the frame dropdown display when the main variant size changes
   * @param {string} newVariantTitle - The new variant title (e.g., "100x140", "A4")
   */
  function updateFrameDropdownDisplay(newVariantTitle) {
    if (!window.frameSelection.isFrameSelected) return;
    
    const selectedOption = document.querySelector('.selected-option');
    if (!selectedOption || selectedOption.classList.contains('none')) return;
    
    const frameHandle = selectedOption.getAttribute('data-handle');
    const frameTitle = selectedOption.getAttribute('data-title');
    
    if (!frameHandle || !frameTitle) return;
    
    // Use frameData for frame product
    let frameProduct = frameData[frameHandle];
    if (frameProduct) {
      let matchedVariant = frameProduct.variants.find(
        variant => variant.title === newVariantTitle
      );
      
      if (matchedVariant) {
        // Update frame selection state
        window.frameSelection.framePrice = parseFloat(matchedVariant.price);
        window.frameSelection.frameComparePrice = parseFloat(matchedVariant.compare_at_price || 0);
        window.frameSelection.frameVariant = matchedVariant.title;
        window.frameSelection.frameId = matchedVariant.id;
        
        // Update price state
        window.priceState.framePrice = window.frameSelection.framePrice;
        window.priceState.frameComparePrice = window.frameSelection.frameComparePrice;
        
        // Trigger price update to sync UI
        window.priceState.updatePrices();
        
        // Format the variant title
        let updatedMatchedVariantTitle;
        updatedMatchedVariantTitle = window.peletonSizeLabel(matchedVariant.title, 'short');
        
        // Get glass type
        let variantMetaValue;
        document.querySelectorAll('.variant-meta-content').forEach(variantMeta => {
          let variantMetaTitle = variantMeta.getAttribute('data-title');
          if (matchedVariant.title == variantMetaTitle) {
            variantMetaValue = variantMeta.getAttribute('data-glass');
          }
        });
        
        // Update the selected option display
        const selectedFrameImage = selectedOption.querySelector('img').getAttribute('src');
        selectedOption.innerHTML = `<img src="${selectedFrameImage}">
          <div class="option--details">
            <span class="option-title">${frameTitle} - 
              <span class="selected-frame-variant" data-size="${matchedVariant.title}">${updatedMatchedVariantTitle} </span>
            </span>
            <div class="option-frame-detail">
              <span class="glass-type">(${t(
                'product.glass_type',
                'Glass:'
              )} <span class="selected-glass">${variantMetaValue}</span>) + 
                <span class="frame-price">
                  ${Woolman.Utils.formatMoney(
                    matchedVariant.price,
                    theme.settings.moneyFormat
                  )}
                </span>
              </span>
            </div>
          </div>`;
        
        selectedOption.setAttribute('value', matchedVariant.id);
        
        // Update all dropdown options for the new variant
        updateAllDropdownOptions(newVariantTitle);
        
        log('Frame dropdown display updated for variant:', newVariantTitle, matchedVariant);
      } else {
        log('No matching frame variant found for:', newVariantTitle, 'Reverting to No Frame');
        // Revert to "No Frame" state when variant is not available for selected frame
        resetToNoFrame();
      }
    }
  }

  // ---------------
  // Dropdown Variant Price Update
  // ---------------
  // Price update for dropdown variants
  if (variantSelect) {
    /**
     * Handles variant selection changes from the dropdown.
     *
     * Fetches the product data for the selected variant, updates the price state,
     * and triggers a UI price update. Ensures only the latest request is processed
     * to avoid race conditions from rapid user input.
     *
     * Side Effects:
     * - Updates window.priceState with the selected variant's price and compare price.
     * - Triggers window.priceState.updatePrices() to update all price displays.
     * - Updates window.latestVariantId for tracking.
     */
    log('Attaching change event to variantSelect');
    variantSelect.addEventListener('change', function () {
      log('variantSelect changed', this.value);
      let selectedOption = this.value;
      let selectMainContainer = this.closest('variant-selects');
      let selectPrdHandle = selectMainContainer.getAttribute('data-handle');
      log('Fetching product for handle', selectPrdHandle);

      // Create a unique request identifier to prevent race conditions
      const requestId = Date.now();
      window.latestVariantSelectRequest = requestId;

      cachedFetch(`/products/${selectPrdHandle}.js`)
        .then(product => {
          // Only process the response if it matches the latest request
          if (window.latestVariantSelectRequest !== requestId) {
            log('Skipping outdated variant select response', requestId);
            return;
          }

          // Find the variant object matching the selected option
          let currentVariant = product.variants.find(variant => variant.title === selectedOption);
          log('Current variant found', currentVariant);
          if (!currentVariant) return;

          // Track the latest variant ID for other logic
          window.latestVariantId = currentVariant.id;

          // Update the global price state (ensures frame data is preserved)
          window.priceState.variantPrice = currentVariant.price;
          window.priceState.variantComparePrice = currentVariant.compare_at_price;

          // Update frame dropdown display if frame is selected (this may update frame prices)
          if (window.frameSelection.isFrameSelected) {
            const selectedOption = document.querySelector('.selected-option');
            if (selectedOption && !selectedOption.classList.contains('none')) {
              updateFrameDropdownDisplay(currentVariant.title);
            }
          } else {
            // If no frame selected, still update dropdown options for correct prices/availability
            updateAllDropdownOptions(currentVariant.title);
            // Then trigger price update
            window.priceState.updatePrices();
          }

          // Update available text count for dropdown options
          setTimeout(() => {
            const visibleCount = Array.from(document.querySelectorAll('.dropdown-option')).filter(
              opt => opt.style.display !== 'none'
            ).length;
            let actualCount = visibleCount - 1; // Subtract 1 for "No Frame" option
            log('Dropdown options updated via dropdown select, visible count', actualCount);
            if (availableText) {
              const optionText = actualCount == 1 ? 
                t('product.frame_picker.one_option_available', 'option available') :
                t('product.frame_picker.options_available', 'options available');
              
              log('Dropdown variant change - Translation result:', optionText);
              log('Dropdown variant change - Setting availableText to:', `${actualCount} ${optionText}`);
              
              availableText.textContent = `${actualCount} ${optionText}`;
            }
          }, 50); // Small delay to ensure dropdown options are updated first

          log('Prices updated via variantSelect', {
            variantId: currentVariant.id,
            variant: currentVariant.price,
            frame: window.frameSelection.isFrameSelected ? window.frameSelection.framePrice : 0,
            total: window.priceState.totalPrice,
          });
        });
    });
  }

  if (dropdownContainer) {
    log('Dropdown container found');
    if (customBtn) {
      priceContainer = customBtn.querySelector('.total-price');
      comparePriceContainer = customBtn.querySelector('.total-compare-price');
    }

    const sizePrice = document.querySelector('.size-total-price');

    let price = 0,
      comparePrice = 0,
      totalPrice = 0,
      totalComparePrice = 0,
      selectedFramePrice = 0;

    // Update available options count
    if (availableText && dropdownOptions.length > 0) {
      // Count visible options (excluding hidden 100x140 if not dk)
      const visibleOptions = Array.from(dropdownOptions).filter(opt => opt.style.display !== 'none').length;
      let actualCount = visibleOptions - 1; // Subtract 1 for "No Frame" option
      log('Setting available options text', {visibleOptions, dropdownOptionsLength: dropdownOptions.length, actualCount});
      log('Available text element', availableText);
      
      const optionText = actualCount == 1 ? 
        t('product.frame_picker.one_option_available', 'option available') :
        t('product.frame_picker.options_available', 'options available');
      
      log('Translation result:', optionText);
      log('Setting availableText to:', `${actualCount} ${optionText}`);
      
      availableText.textContent = `${actualCount} ${optionText}`;
    }

    // Toggle dropdown list visibility
    dropdownContainer.addEventListener('click', e => {
      log('Dropdown container clicked');
      dropdownList.classList.toggle('open');
      e.stopPropagation();
    });

    // Close dropdown when clicking outside
    document.addEventListener('click', () => {
      log('Document clicked, closing dropdown');
      dropdownList.classList.remove('open');
    });

    /**
     * Handles changes to the product variant selection (radio inputs).
     *
     * This function is responsible for updating the UI and internal state when a user selects a different product variant (such as size or frame option).
     * It updates the price, frame selection, and availability display based on the selected variant.
     *
     * Main responsibilities:
     * - Fetches the relevant product and frame data for the selected variant.
     * - Updates global price state (`window.priceState`) and frame selection state (`window.frameSelection`).
     * - Updates the DOM to reflect the selected variant's price, frame, and glass type.
     * - Handles inventory and delivery estimate display for out-of-stock variants.
     * - Ensures dropdown options and their prices are updated for all frames.
     *
     * @param {HTMLInputElement} input - The radio input element that triggered the change event. Should represent a product variant.
     *
     * Side effects:
     * - Modifies DOM elements for price, frame, and delivery display.
     * - Updates global state objects: `window.priceState` and `window.frameSelection`.
     * - May trigger asynchronous fetches for product/frame data.
     *
     * Notes:
     * - Relies on global variables and helpers: `cachedFetch`, `frameData`, `dropdownOptions`, `updatePrices`, and translation helper `t()`.
     * - Handles both main product and frame product logic, including fallback fetches if frame data is not cached.
     * - Updates dropdown options to show only available variants.
     */
    function handleVariantChange(input) {
      log('handleVariantChange called', input.value);
      let selectedVariant = input.value;
      let variantContainer = input.closest('variant-radios');
      const optionVariant = document.querySelector('.selected-option');
      const selectedTitle = optionVariant.getAttribute('data-title');
      const selectedHandle = optionVariant.getAttribute('data-handle');
      const frameVariant = optionVariant.getAttribute('data-variant');
      let selectedFrameImage, selectedFrameTitle;

      if (!optionVariant.classList.contains('none')) {
        selectedFrameImage = optionVariant.querySelector('img').getAttribute('src');
        selectedFrameTitle = optionVariant.querySelector('.option-title').textContent;
      }

      if (!variantContainer) {
        log('No variantContainer found');
        return;
      }
      let productHandle = variantContainer.getAttribute('data-handle');
      if (!productHandle) {
        log('No productHandle found');
        return;
      }

      // Use cachedFetch for main product
      cachedFetch(`/products/${productHandle}.js`)
        .then(product => {
          let currentVariant = product.variants.find(variant => variant.title === selectedVariant);
          log('Current variant for radios', currentVariant);
          if (!currentVariant) return;

          // Update the price state instead of local variables
          window.priceState.variantPrice = currentVariant.price;
          window.priceState.variantComparePrice = currentVariant.compare_at_price;

          // If we have a frame selected, the framePrice properties will already be set
          // If not, they'll be 0 because of the initial state
          log('Price state before update', {
            variant: window.priceState.variantPrice,
            frame: window.priceState.framePrice,
            isFrameSelected: window.frameSelection.isFrameSelected,
          });

          // This call respects any existing frame selection
          window.priceState.updatePrices();

          if (optionVariant.classList.contains('none')) {
            log('Updated prices for none frame');
          }

          if (!optionVariant.classList.contains('none')) {
            log('Fetching selected frame handle', selectedHandle);
            // Use frameData for frame product
            let frameProduct = frameData[selectedHandle];
            if (frameProduct) {
              let matchedVariant = frameProduct.variants.find(
                variant => variant.title === selectedVariant
              );
              log('Matched variant for frame', matchedVariant);

              // We have a frame selected and variant changed - update frame prices
              if (matchedVariant) {
                // Update frame price in frameSelection
                window.frameSelection.framePrice = parseFloat(matchedVariant.price);
                window.frameSelection.frameComparePrice = parseFloat(
                  matchedVariant.compare_at_price || 0
                );
                // Update priceState with frame data
                window.priceState.framePrice = window.frameSelection.framePrice;
                window.priceState.frameComparePrice = window.frameSelection.frameComparePrice;
                // Update prices
                window.priceState.updatePrices();

                // Check inventory directly from the variant data
                log(
                  'Checking inventory from API response:',
                  matchedVariant.inventory_policy,
                  matchedVariant.inventory_quantity
                );

                if (
                  matchedVariant.inventory_policy == 'continue' &&
                  matchedVariant.inventory_quantity <= 0
                ) {
                  // Show extended delivery for out-of-stock items
                  if (typeof window.updateDeliveryEstimate === 'function') {
                    window.updateDeliveryEstimate(true);
                  }
                } else {
                  // Show dynamic delivery calculation for in-stock items
                  if (typeof window.updateDeliveryEstimate === 'function') {
                    window.updateDeliveryEstimate(false);
                  }
                }
              }

              if (!matchedVariant) {
                log('No matched variant, resetting to No Frame');
                // Reset to "No Frame" state properly
                optionVariant.classList.add('none');
                optionVariant.setAttribute('data-handle', 'none');
                optionVariant.removeAttribute('data-title');
                optionVariant.classList.add('none-selected');
                optionVariant.removeAttribute('data-variant');
                customBtn.style.display = 'none';
                mainAtc.style.display = 'inline-flex';

                optionVariant.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 60 60" fill="none">
                    <rect width="60" height="60" fill="#F1F1F1"/>
                    <path d="M59 1L1 59" stroke="white" stroke-width="2" stroke-linecap="round"/>
                  </svg>
                <div class="option--details">
                  <span class="option-title">${t('product.frame_picker.no_frame', 'No Frame')}
                  </span>
                  <div class="option-frame-detail">
                    <span class="glass-type">${t(
                      'product.frame_picker.frame_own_print',
                      'You will frame your own print'
                    )}
                    </span>
                  </div>
                </div>`;

                // Show dynamic delivery calculation when frame is cleared
                if (typeof window.updateDeliveryEstimate === 'function') {
                  window.updateDeliveryEstimate(false);
                }

                // Use unified price update function with compare price
                updatePrices(price, 0, comparePrice);

                // Update global frameSelection object
                window.frameSelection.isFrameSelected = false;
                window.frameSelection.framePrice = 0;
                window.frameSelection.frameHandle = null;
                window.frameSelection.frameTitle = null;
                window.frameSelection.frameVariant = null;
                window.frameSelection.frameId = null;
                window.frameSelection.frameComparePrice = 0;
              } else {
                selectedFramePrice = matchedVariant.price;
                priceContainer.setAttribute('data-price', price);
                totalPrice = parseFloat(price) + parseFloat(selectedFramePrice);

                // Sync frame selection state with chosen variant
                window.frameSelection.isFrameSelected = true;
                window.frameSelection.framePrice = parseFloat(selectedFramePrice);
                window.frameSelection.frameComparePrice = parseFloat(
                  matchedVariant.compare_at_price || 0
                );
                window.frameSelection.frameHandle = selectedHandle;
                window.frameSelection.frameTitle = selectedTitle;
                window.frameSelection.frameVariant = matchedVariant.title;
                window.frameSelection.frameId = matchedVariant.id;

                // Always use updatePrices to ensure UI reflects current selection
                const matchedFrameComparePrice = matchedVariant.compare_at_price || 0;
                updatePrices(price, selectedFramePrice, comparePrice, matchedFrameComparePrice);

                optionVariant.setAttribute('value', matchedVariant.id);
                let variantMetaValue;
                document.querySelectorAll('.variant-meta-content').forEach(variantMeta => {
                  let variantMetaTitle = variantMeta.getAttribute('data-title');
                  if (matchedVariant.title == variantMetaTitle) {
                    variantMetaValue = variantMeta.getAttribute('data-glass');
                  }
                });
                let updatedMatchedVariantTitle;
                log('v', matchedVariant.title);
                updatedMatchedVariantTitle = window.peletonSizeLabel(matchedVariant.title, 'short');
                optionVariant.innerHTML = `<img src="${selectedFrameImage}">
                  <div class="option--details">
                    <span class="option-title">${selectedTitle} - 
                      <span class="selected-frame-variant" data-size="${matchedVariant.title}">${updatedMatchedVariantTitle} </span>
                    </span>
                    <div class="option-frame-detail">
                      <span class="glass-type">(${t(
                        'product.glass_type',
                        'Glass:'
                      )} <span class="selected-glass">${variantMetaValue}</span>) + 
                        <span class="frame-price">
                          ${Woolman.Utils.formatMoney(
                            matchedVariant.price,
                            theme.settings.moneyFormat
                          )}
                        </span>
                      </span>
                    </div>
                  </div>`;
              }
            } else {
              // fallback to cachedFetch if not found in frameData
              cachedFetch(`/products/${selectedHandle}.js`).then(frameProduct => {
                let matchedVariant = frameProduct.variants.find(
                  variant => variant.title === selectedVariant
                );
                log('Matched variant for frame', matchedVariant);

                // Similar to above, update the frame price if frame is found in fetch
                if (matchedVariant) {
                  // Update frame price in frameSelection
                  window.frameSelection.framePrice = parseFloat(matchedVariant.price);
                  window.frameSelection.frameComparePrice = parseFloat(
                    matchedVariant.compare_at_price || 0
                  );
                  // Update priceState with frame data
                  window.priceState.framePrice = window.frameSelection.framePrice;
                  window.priceState.frameComparePrice = window.frameSelection.frameComparePrice;
                  // Update prices
                  window.priceState.updatePrices();

                  // Check inventory directly from the variant data
                  log(
                    'Checking inventory from API response:',
                    matchedVariant.inventory_policy,
                    matchedVariant.inventory_quantity
                  );

                  if (
                    matchedVariant.inventory_policy == 'continue' &&
                    matchedVariant.inventory_quantity <= 0
                  ) {
                    // Show extended delivery for out-of-stock items
                    if (typeof window.updateDeliveryEstimate === 'function') {
                      window.updateDeliveryEstimate(true);
                    }
                  } else {
                    // Show dynamic delivery calculation for in-stock items
                    if (typeof window.updateDeliveryEstimate === 'function') {
                      window.updateDeliveryEstimate(false);
                    }
                  }
                }

                if (!matchedVariant) {
                  log('No matched variant, resetting to No Frame');
                  // Reset to "No Frame" state properly
                  optionVariant.classList.add('none');
                  optionVariant.setAttribute('data-handle', 'none');
                  optionVariant.removeAttribute('data-title');
                  optionVariant.classList.add('none-selected');
                  optionVariant.removeAttribute('data-variant');
                  customBtn.style.display = 'none';
                  mainAtc.style.display = 'inline-flex';

                  optionVariant.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 60 60" fill="none">
                      <rect width="60" height="60" fill="#F1F1F1"/>
                      <path d="M59 1L1 59" stroke="white" stroke-width="2" stroke-linecap="round"/>
                    </svg>
                  <div class="option--details">
                    <span class="option-title">${t('product.frame_picker.no_frame', 'No Frame')}
                    </span>
                    <div class="option-frame-detail">
                      <span class="glass-type">${t(
                        'product.frame_picker.frame_own_print',
                        'You will frame your own print'
                      )}
                      </span>
                    </div>
                  </div>`;

                  // Show dynamic delivery calculation when frame is cleared
                  if (typeof window.updateDeliveryEstimate === 'function') {
                    window.updateDeliveryEstimate(false);
                  }

                  // Use unified price update function with compare price
                  updatePrices(price, 0, comparePrice);

                  // Update global frameSelection object
                  window.frameSelection.isFrameSelected = false;
                  window.frameSelection.framePrice = 0;
                  window.frameSelection.frameHandle = null;
                  window.frameSelection.frameTitle = null;
                  window.frameSelection.frameVariant = null;
                  window.frameSelection.frameId = null;
                  window.frameSelection.frameComparePrice = 0;
                } else {
                  selectedFramePrice = matchedVariant.price;
                  priceContainer.setAttribute('data-price', price);
                  totalPrice = parseFloat(price) + parseFloat(selectedFramePrice);

                  // Update global frameSelection object
                  window.frameSelection.isFrameSelected = true;
                  window.frameSelection.framePrice = parseFloat(selectedFramePrice);
                  window.frameSelection.frameComparePrice = parseFloat(
                    matchedVariant.compare_at_price || 0
                  );
                  window.frameSelection.frameHandle = selectedHandle;
                  window.frameSelection.frameTitle = selectedTitle;
                  window.frameSelection.frameVariant = matchedVariant.title;
                  window.frameSelection.frameId = matchedVariant.id;

                  // Use the unified update function for combined prices with compare price
                  const matchedFrameComparePrice = matchedVariant.compare_at_price || 0;
                  updatePrices(price, selectedFramePrice, comparePrice, matchedFrameComparePrice);

                  optionVariant.setAttribute('value', matchedVariant.id);
                  let variantMetaValue;
                  document.querySelectorAll('.variant-meta-content').forEach(variantMeta => {
                    let variantMetaTitle = variantMeta.getAttribute('data-title');
                    if (matchedVariant.title == variantMetaTitle) {
                      variantMetaValue = variantMeta.getAttribute('data-glass');
                    }
                  });
                  let updatedMatchedVariantTitle;
                  log('v', matchedVariant.title);
                  updatedMatchedVariantTitle = window.peletonSizeLabel(matchedVariant.title, 'short');
                  optionVariant.innerHTML = `<img src="${selectedFrameImage}">
                    <div class="option--details">
                      <span class="option-title">${selectedTitle} - 
                        <span class="selected-frame-variant" data-size="${matchedVariant.title}">${updatedMatchedVariantTitle} </span>
                      </span>
                      <div class="option-frame-detail">
                        <span class="glass-type">(${t(
                          'product.glass_type',
                          'Glass:'
                        )} <span class="selected-glass">${variantMetaValue}</span>) + 
                          <span class="frame-price">
                            ${Woolman.Utils.formatMoney(
                              matchedVariant.price,
                              theme.settings.moneyFormat
                            )}
                          </span>
                        </span>
                      </div>
                    </div>`;
                }
              });
            }
          }
        })
        .catch(error => {
          log('Error in radios fetch', error);
        });

      const fetchPromises = [];

      dropdownOptions.forEach(option => {
        const optionHandle = option.getAttribute('data-handle');
        if (!optionHandle) return;

        const optionVariant = option.querySelector('.selected-frame-variant');
        const optionSelectGlass = option.querySelector('.selected-glass');
        const optionPrice = option.querySelector('.frame-price');

        // Use frameData for frame product
        let frameProduct = frameData[optionHandle];
        if (frameProduct) {
          let matchedVariant = frameProduct.variants.find(
            variant => variant.title === selectedVariant
          );
          
          // Hide 100x140 unless this market allows oversized frames
          if (matchedVariant && matchedVariant.title === '100x140' && !oversizedFramesAllowed) {
            option.style.display = 'none';
            return;
          }
          
          log('Dropdown option fetch', optionHandle, matchedVariant);
          if (!matchedVariant) {
            option.style.display = 'none';
            return;
          }
          let variantMetaValue;
          document.querySelectorAll('.variant-meta-content').forEach(variantMeta => {
            let variantMetaTitle = variantMeta.getAttribute('data-title');
            if (matchedVariant.title == variantMetaTitle) {
              variantMetaValue = variantMeta.getAttribute('data-glass');
            }
          });
          let updatedMatchedVariantTitle;
          updatedMatchedVariantTitle = window.peletonSizeLabel(matchedVariant.title, 'short');
          option.style.display = 'flex';
          optionSelectGlass.textContent = variantMetaValue;
          optionVariant.textContent = updatedMatchedVariantTitle;
          optionVariant.dataset.size = matchedVariant.title;
          option.setAttribute('value', matchedVariant.id);
          option.setAttribute('data-price', matchedVariant.price);
          option.setAttribute('data-compare', matchedVariant.compare_at_price);
          optionPrice.textContent = Woolman.Utils.formatMoney(
            matchedVariant.price,
            theme.settings.moneyFormat
          );
        } else {
          // fallback to cachedFetch if not found in frameData
          const fetchPromise = cachedFetch(`/products/${optionHandle}.js`).then(product => {
            let matchedVariant = product.variants.find(
              variant => variant.title === selectedVariant
            );
            
            // Hide 100x140 unless this market allows oversized frames
            if (matchedVariant && matchedVariant.title === '100x140' && !oversizedFramesAllowed) {
              option.style.display = 'none';
              return;
            }
            
            log('Dropdown option fetch', optionHandle, matchedVariant);
            if (!matchedVariant) {
              option.style.display = 'none';
              return;
            }
            let variantMetaValue;
            document.querySelectorAll('.variant-meta-content').forEach(variantMeta => {
              let variantMetaTitle = variantMeta.getAttribute('data-title');
              if (matchedVariant.title == variantMetaTitle) {
                variantMetaValue = variantMeta.getAttribute('data-glass');
              }
            });
            let updatedMatchedVariantTitle;
            updatedMatchedVariantTitle = window.peletonSizeLabel(matchedVariant.title, 'short');
            option.style.display = 'flex';
            optionSelectGlass.textContent = variantMetaValue;
            optionVariant.textContent = updatedMatchedVariantTitle;
            optionVariant.dataset.size = matchedVariant.title;
            option.setAttribute('value', matchedVariant.id);
            option.setAttribute('data-price', matchedVariant.price);
            option.setAttribute('data-compare', matchedVariant.compare_at_price);
            optionPrice.textContent = Woolman.Utils.formatMoney(
              matchedVariant.price,
              theme.settings.moneyFormat
            );
          });
          fetchPromises.push(fetchPromise);
        }
      });

      Promise.all(fetchPromises).then(() => {
        const visibleCount = Array.from(document.querySelectorAll('.dropdown-option')).filter(
          opt => opt.style.display !== 'none'
        ).length;
        let actualCount = visibleCount - 1;
        log('Dropdown options updated, visible count', actualCount);
        log('Available text element', availableText);
        if (actualCount == 1) {
          availableText.textContent = `${actualCount} ${t(
            'product.frame_picker.one_option_available',
            'option available'
          )}`;
        } else {
          availableText.textContent = `${actualCount} ${t(
            'product.frame_picker.options_available',
            'options available'
          )}`;
        }
      });
    }

    // Attach event listener and also trigger on load
    document.querySelectorAll(".product-variant-options input[type='radio']").forEach(input => {
      input.addEventListener('change', function () {
        log('Radio input changed', input.value);
        handleVariantChange(input);
      });

      // Trigger on load if checked
      if (input.checked) {
        log('Radio input checked on load', input.value);
        handleVariantChange(input);
      }
    });

    // Handle option selection
    dropdownOptions.forEach(option => {
      // Hide 100x140 options on initial load unless this market allows oversized frames
      // Compare on the raw size (data-size), not the label: the label may be in inches.
      const optionVariantEl = option.querySelector('.selected-frame-variant');
      const optionVariantSize = optionVariantEl
        ? (optionVariantEl.dataset.size || optionVariantEl.textContent.replace(' cm', '')).trim()
        : '';
      if (optionVariantSize === '100x140' && !oversizedFramesAllowed) {
        option.style.display = 'none';
      }
      
      option.addEventListener('click', e => {
        log('Dropdown option clicked', option.getAttribute('data-title'));
        const optionTitle = option.getAttribute('data-title');
        const mediaSlider = document.querySelector('.product-media-slider');
        e.stopPropagation();

        const isNoFrame = option.getAttribute('data-value') === 'none';
        selectedOption.classList.toggle('none', isNoFrame);

        if (isNoFrame) {
          log('No frame selected');
          selectedOption.classList.add('none-selected');
          selectedOption.setAttribute('data-handle', 'none');
          selectedOption.removeAttribute('data-title');
          selectedOption.removeAttribute('data-variant');
          customBtn.style.display = 'none';
          mainAtc.style.display = 'inline-flex';
          selectedOption.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 60 60" fill="none">
                <rect width="60" height="60" fill="#F1F1F1"/>
                <path d="M59 1L1 59" stroke="white" stroke-width="2" stroke-linecap="round"/>
              </svg>
              <div class="option--details">
                <span class="option-title">${t('product.frame_picker.no_frame', 'No Frame')}
                </span>
                <div class="option-frame-detail">
                  <span class="glass-type">${t(
                    'product.frame_picker.frame_own_print',
                    'You will frame your own print'
                  )}
                  </span>
                </div>
              </div>`;

          // Update global frameSelection object
          window.frameSelection.isFrameSelected = false;
          window.frameSelection.framePrice = 0;
          window.frameSelection.frameHandle = 'none';
          window.frameSelection.frameTitle = null;
          window.frameSelection.frameVariant = null;
          window.frameSelection.frameId = null;
          window.frameSelection.frameComparePrice = 0;

          // Show dynamic delivery calculation when no frame is selected
          if (typeof window.updateDeliveryEstimate === 'function') {
            window.updateDeliveryEstimate(false);
          }

          mediaSlider.scrollToSlide(0);
          // Frame compositing: "no frame" shows the plain poster on slide 1.
          if (window.PeletonFrames && document.querySelector('.product-media-slider [data-pf-switch]')) {
            window.PeletonFrames.select('none');
          }

          const selectedMainVariantHandle = getCurrentProductHandle();
          const selectedMainVariant = getCurrentVariantValue();
          
          if (selectedMainVariantHandle && selectedMainVariant) {
            log('Fetching for no frame, main variant', selectedMainVariant);
            fetch(`/products/${selectedMainVariantHandle}.js`)
              .then(response => response.json())
              .then(product => {
                let matchedVariant = product.variants.find(
                  variant => variant.title === selectedMainVariant
                );
                if (!matchedVariant) return;

                let totalAddedPrice = matchedVariant.price;
                // Use unified price update function with possible compare price
                updatePrices(totalAddedPrice, 0, matchedVariant.compare_at_price);
              })
              .catch(error => log('Error in no frame fetch', error));
          }
        } else {
          log('Frame selected', optionTitle);
          selectedOption.classList.remove('none-selected');
          customBtn.style.display = 'inline-flex';
          mainAtc.style.display = 'none';

          const optionHandle = option.getAttribute('data-handle');
          const optionVariantNode = option.querySelector('.selected-frame-variant');
          const optionVariant = optionVariantNode.textContent;
          // Raw size for matching (the label may be in inches)
          const optionVariantRaw = (optionVariantNode.dataset.size || optionVariant.replace(' cm', '')).trim();
          const framePrice = option.getAttribute('data-price');
          const frameComparePrice = option.getAttribute('data-compare');
          const optionImage = option.querySelector('img').getAttribute('src');
          const frameId = option.getAttribute('value');
          selectedOption.setAttribute('data-handle', optionHandle);
          selectedOption.setAttribute('data-title', optionTitle);
          selectedOption.setAttribute('data-variant', optionVariant);

          // Update global frameSelection object
          window.frameSelection.isFrameSelected = true;
          window.frameSelection.framePrice = parseFloat(framePrice);
          window.frameSelection.frameComparePrice = parseFloat(frameComparePrice || 0);
          window.frameSelection.frameHandle = optionHandle;
          window.frameSelection.frameTitle = optionTitle;
          window.frameSelection.frameVariant = optionVariant;
          window.frameSelection.frameId = frameId;

          // Check inventory using frameData
          const frameDataObj = window.frameData[optionHandle];
          if (frameDataObj && frameDataObj.variants) {
            // Find the matching variant by ID
            const frameVariant = frameDataObj.variants.find(v => v.id == frameId);
            if (frameVariant) {
              log(
                'Checking inventory for selected frame with frameData structure',
                frameVariant.inventory_policy,
                frameVariant.inventory_quantity
              );

              if (
                frameVariant.inventory_policy == 'continue' &&
                frameVariant.inventory_quantity <= 0
              ) {
                // Show extended delivery for out-of-stock frame variants
                if (typeof window.updateDeliveryEstimate === 'function') {
                  window.updateDeliveryEstimate(true);
                }
              } else {
                // Show dynamic delivery calculation for in-stock frame variants
                if (typeof window.updateDeliveryEstimate === 'function') {
                  window.updateDeliveryEstimate(false);
                }
              }
            }
          } else {
            // Fallback to variant JSON if frameData isn't working
            const variantJsonData = document.querySelector('.variant-json').textContent;
            let parsedData = [];
            try {
              parsedData = JSON.parse(variantJsonData);
            } catch (e) {
              log('[PEL] Failed to parse variant JSON data', e);
            }

            parsedData.forEach(variant => {
              if (variant.variant_id == frameId) {
                log(
                  'Fallback: checking inventory from variantJSON',
                  variant.inventory_policy,
                  variant.inventory_quantity
                );
                if (variant.inventory_policy == 'continue' && variant.inventory_quantity <= 0) {
                  // Show extended delivery for out-of-stock variants (fallback)
                  if (typeof window.updateDeliveryEstimate === 'function') {
                    window.updateDeliveryEstimate(true);
                  }
                } else {
                  // Show dynamic delivery calculation for in-stock variants (fallback)
                  if (typeof window.updateDeliveryEstimate === 'function') {
                    window.updateDeliveryEstimate(false);
                  }
                }
              }
            });
          }

          // Frame compositing: if slide 1 is a composite, recolour it instead of jumping to baked slides.
          var pfKey =
            window.PeletonFrames && document.querySelector('.product-media-slider [data-pf-switch]')
              ? window.PeletonFrames.keyForHandle(optionHandle)
              : null;
          if (pfKey) {
            window.PeletonFrames.select(pfKey);
            mediaSlider.scrollToSlide(0);
          } else if (optionHandle == 'lys-egetraes-ramme') {
            mediaSlider.scrollToSlide(0);
          } else if (optionHandle == 'mork-egetrae-ramme') {
            var _totalSlides = mediaSlider.slides ? mediaSlider.slides.length : 0;
            mediaSlider.scrollToSlide(_totalSlides - 2);
          } else if (optionHandle == 'sort-ramme') {
            var _totalSlides = mediaSlider.slides ? mediaSlider.slides.length : 0;
            mediaSlider.scrollToSlide(_totalSlides - 3);
          }
          let variantMetaSelect;
          document.querySelectorAll('.variant-meta-content').forEach(variantMeta => {
            let variantMetaTitle = variantMeta.getAttribute('data-title');
            if (optionVariantRaw == variantMetaTitle) {
              variantMetaSelect = variantMeta.getAttribute('data-glass');
            }
          });

          selectedOption.innerHTML = `<img src="${optionImage}">
              <div class="option--details">
                <span class="option-title">${optionTitle} - <span class="selected-frame-variant" data-size="${optionVariantRaw}">${optionVariant}</span></span>
                <div class="option-frame-detail">
                  <span class="glass-type">(${t(
                    'product.glass_type',
                    'Glass:'
                  )} <span class="selected-glass">${variantMetaSelect}</span>) + <span class="frame-price">${Woolman.Utils.formatMoney(
            framePrice,
            theme.settings.moneyFormat
          )}</span></span>
                </div>
              </div>`;

          const selectedMainVariantHandle = getCurrentProductHandle();
          const selectedMainVariant = getCurrentVariantValue();
          
          if (selectedMainVariantHandle && selectedMainVariant) {
            log('Fetching for frame, main variant', selectedMainVariant);
            fetch(`/products/${selectedMainVariantHandle}.js`)
              .then(response => response.json())
              .then(product => {
                let matchedVariant = product.variants.find(
                  variant => variant.title === selectedMainVariant
                );
                if (!matchedVariant) return;

                // Use unified price update function with frame price and compare prices
                const frameComparePrice = option.getAttribute('data-compare') || 0;
                updatePrices(
                  matchedVariant.price,
                  framePrice,
                  matchedVariant.compare_at_price,
                  frameComparePrice
                );
              })
              .catch(error => log('Error in frame fetch', error));
          }

          // Compare price is now handled in updatePrices
          selectedOption.setAttribute('value', frameId);

          // Prevent duplicate listeners by removing any previous handler first
          const customBtnEl = document.querySelector('.custom-btn');
          if (customBtnEl) {
            if (customBtnEl._customAddToCartHandler) {
              customBtnEl.removeEventListener('click', customBtnEl._customAddToCartHandler);
            }
            customBtnEl._customAddToCartHandler = function () {
              log('Custom add to cart clicked', selectedOption.getAttribute('value'));
              addToCart(selectedOption.getAttribute('value'));
            };
            customBtnEl.addEventListener('click', customBtnEl._customAddToCartHandler);
          }
        }

        dropdownList.classList.remove('open');
      });
    });

    async function addToCart(variantId, quantity = 1) {
      log('addToCart called', variantId, quantity);
      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({ id: variantId, quantity: quantity }),
        });
        mainAtc.click();
        log('Add to cart response', response);
        return await response.json();
      } catch (error) {
        log('Add to Cart Error:', error);
        // Show user feedback on error
        const btn = document.querySelector('.custom-btn');
        if (btn) {
          const originalText = btn.textContent;
          btn.textContent = typeof t === 'function' ? t('cart.error', 'Error adding to cart') : 'Error adding to cart';
          btn.classList.add('error');
          setTimeout(() => {
            btn.textContent = typeof t === 'function' ? t('cart.add_to_cart', 'Add to cart') : 'Add to cart';
            btn.classList.remove('error');
          }, 2000);
        }
      }
    }
  }

  // Product Media JS
  const mediaContainer = document.querySelector('.product-media-slider .gridy-track');
  const prevButton = document.querySelector('.slider-prev-arrow');
  const nextButton = document.querySelector('.slider-next-arrow');
  const activeSlideCountEl = document.querySelector('.active-slide-count');
  const totalSlideCountEl = document.querySelector('.total-slide-count');
  const ogPrevBtn = mediaContainer?.parentElement.querySelector('button[data-direction="prev"]');
  const ogNextBtn = mediaContainer?.parentElement.querySelector('button[data-direction="next"]');

  if (
    mediaContainer &&
    prevButton &&
    nextButton &&
    activeSlideCountEl &&
    totalSlideCountEl &&
    ogPrevBtn &&
    ogNextBtn
  ) {
    // Get all media items
    const mediaItems = mediaContainer.querySelectorAll('.product-media');
    if (mediaItems.length > 0) {
      const updatedMediaLength = mediaItems.length;

      // Set total slide count with translated "of"
      const ofText = document.querySelector('.slider-of');
      if (ofText) {
        ofText.textContent = t('general.slider.of', 'of');
      }
      totalSlideCountEl.textContent = updatedMediaLength;

      // Initialize active slide index
      let currentSlideIndex = 0;

      function updateArrowVisibility() {
        // Hide previous arrow when on first slide
        prevButton.style.visibility = currentSlideIndex === 0 ? 'hidden' : 'visible';
        // Hide next arrow when on last slide
        nextButton.style.visibility =
          currentSlideIndex === updatedMediaLength - 1 ? 'hidden' : 'visible';
      }

      updateArrowVisibility();

      // Update active slide count
      function updateActiveSlideCount() {
        activeSlideCountEl.textContent = currentSlideIndex + 1;
      }

      // Navigate to next slide
      function nextSlide() {
        currentSlideIndex = (currentSlideIndex + 1) % updatedMediaLength;
        scrollToSlide();
        updateArrowVisibility();
      }

      // Navigate to previous slide
      function prevSlide() {
        currentSlideIndex = (currentSlideIndex - 1 + updatedMediaLength) % updatedMediaLength; // Fixed calculation
        scrollToSlide();
        updateArrowVisibility();
      }

      // Scroll to the current slide
      function scrollToSlide() {
        if (mediaItems[currentSlideIndex]) {
          mediaItems[currentSlideIndex].scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'start',
          });
          updateActiveSlideCount();
        }
      }

      // Add function to media slider to scroll to specific index
      mediaContainer.scrollToSlide = function (index) {
        if (index >= 0 && index < mediaItems.length) {
          currentSlideIndex = index;
          scrollToSlide();
          updateArrowVisibility();
        }
      };

      // Add event listeners to navigation buttons
      nextButton.addEventListener('click', nextSlide);
      prevButton.addEventListener('click', prevSlide);
      ogPrevBtn.addEventListener('click', function () {
        currentSlideIndex = (currentSlideIndex - 1 + updatedMediaLength) % updatedMediaLength;
        updateActiveSlideCount();
      });
      ogNextBtn.addEventListener('click', function () {
        currentSlideIndex = (currentSlideIndex + 1) % updatedMediaLength;
        updateActiveSlideCount();
      });

      // Initial active slide count
      updateActiveSlideCount();

      mediaContainer.addEventListener('scroll', () => {
        let closestIndex = 0;
        let closestDistance = Infinity;

        mediaItems.forEach((item, index) => {
          const rect = item.getBoundingClientRect();
          const distance = Math.abs(rect.left - mediaContainer.getBoundingClientRect().left);
          if (distance < closestDistance) {
            closestDistance = distance;
            closestIndex = index;
          }
        });

        currentSlideIndex = closestIndex;
        updateActiveSlideCount();
        updateArrowVisibility();
      });
    }
  }

  // Size Drawer
  if (window.sizeDrawers && Array.isArray(window.sizeDrawers)) {
    window.sizeDrawers.forEach(drawerConfig => {
      const drawerName = drawerConfig.drawer_name;

      const btn = document.querySelector(`.${drawerName}`);
      const container = document.querySelector(`.size-drawer--main-container.${drawerName}`);

      if (!btn || !container) return;

      const content = container.querySelector('.size-drawer-panel');
      const header = document.querySelector('.site-header');
      const body = document.body;
      const closeBtn = container.querySelector('.drawer-close--btn');

      btn.addEventListener('click', () => {
        container.classList.add('size-drawer--open');
        content.style.transform = 'translate(0)';
        header.classList.add('header-back');
        body.classList.add('overflow-hidden');
      });

      if (closeBtn) {
        closeBtn.addEventListener('click', () => {
          closeDrawer(container, content, header, body);
        });
      }

      container.addEventListener('click', event => {
        if (!content.contains(event.target)) {
          closeDrawer(container, content, header, body);
        }
      });
    });
  }

  function closeDrawer(container, content, header, body) {
    setTimeout(() => {
      content.style.transform = 'translate(100%)';
      header.classList.remove('header-back');
      body.classList.remove('overflow-hidden');
      container.classList.remove('size-drawer--open');
    }, 300);
  }

  // Size variant selector
  const sizeVar = document.querySelector('.product-form-label--Size .product-variant-options');
  if (sizeVar) {
    const allSizeBtn = sizeVar.querySelector('.size-drawer');
    const sizeVariantContainer = sizeVar.querySelector('.prd-variant-Size');

    if (allSizeBtn && sizeVariantContainer) {
      allSizeBtn.addEventListener('click', function () {
        sizeVariantContainer.style.flex = '0 0 100%';
        document.querySelectorAll('.prd-variant-Size label').forEach(variantLabel => {
          variantLabel.style.display = ' block';
        });
        allSizeBtn.style.display = 'none';
      });
    }
  }

  // Gridy Slider Button Disable Functionality
  const gridyPrevButtons = document.querySelectorAll('.gridy-arrows button[data-direction="prev"]');
  if (gridyPrevButtons.length > 0) {
    gridyPrevButtons.forEach(prevBtn => {
      prevBtn.style.visibility = 'hidden';
      prevBtn.disabled = true; // Disable on first load
      const prcPrev = prevBtn.closest('.prc-prev'); // Find closest .prc-prev
      if (prcPrev) prcPrev.classList.add('disabled'); // Disable .prc-prev initially

      const gridyBtnContainer = prevBtn.parentElement;
      if (!gridyBtnContainer) return;

      const gridySlash = gridyBtnContainer.querySelector('.slash');
      if (gridySlash) gridySlash.style.display = 'none';

      const nextBtn = gridyBtnContainer.querySelector('button[data-direction="next"]');
      const slash = gridyBtnContainer.querySelector('.slash');
      if (!nextBtn) return;

      const updatePrevButtonState = () => {
        const gridyContainer = nextBtn.parentElement?.parentElement;
        if (!gridyContainer) return;

        const gridyTrack = gridyContainer.querySelector('.gridy-track');
        if (!gridyTrack || !gridyTrack.children.length) return;

        const firstChild = gridyTrack.children[0];
        const lastChild = gridyTrack.lastElementChild;
        const hasActiveChild = Array.from(gridyTrack.children).some(child =>
          child.classList.contains('active')
        );

        if (firstChild?.classList.contains('active')) {
          prevBtn.disabled = true;
          prevBtn.style.visibility = 'hidden';
          if (prcPrev) prcPrev.classList.add('disabled');
          if (gridySlash) gridySlash.style.display = 'none';
        } else if (hasActiveChild) {
          prevBtn.disabled = false;
          prevBtn.style.visibility = 'visible';
          if (prcPrev) prcPrev.classList.remove('disabled');
          if (gridySlash) gridySlash.style.display = 'block';
        } else {
          prevBtn.style.visibility = 'hidden';
          prevBtn.disabled = true;
          if (prcPrev) prcPrev.classList.add('disabled');
          if (gridySlash) gridySlash.style.display = 'none';
        }
        if (lastChild?.classList.contains('active')) {
          nextBtn.style.visibility = 'hidden';
          nextBtn.disabled = true;
        } else {
          nextBtn.style.visibility = 'visible';
          nextBtn.disabled = false;
        }
      };

      nextBtn.addEventListener('click', updatePrevButtonState);
      prevBtn.addEventListener('click', updatePrevButtonState);

      nextBtn.addEventListener('click', function () {
        const gridyContainer = nextBtn.parentElement?.parentElement;
        if (!gridyContainer) return;

        const gridyTrack = gridyContainer.querySelector('.gridy-track');

        if (gridyTrack) {
          // Prevent MutationObserver leak by disconnecting previous observer
          if (gridyTrack._prevObserver) {
            gridyTrack._prevObserver.disconnect();
          }
          const observer = new MutationObserver(updatePrevButtonState);
          observer.observe(gridyTrack, {
            childList: true,
            attributes: true,
            subtree: true,
          });
          gridyTrack._prevObserver = observer;
        }
        updatePrevButtonState();
      });
    });
  }

  // PRC button container
  const prcBtnContainer = document.querySelector('.prc-main-product');

  if (prcBtnContainer) {
    const prcMainContainer = prcBtnContainer.parentElement;
    const mainRecContainer = prcBtnContainer.parentElement;
    if (!mainRecContainer) return;

    const prcPrev = prcBtnContainer.querySelector('.prc-prev');
    const prcNext = prcBtnContainer.querySelector('.prc-next');
    prcPrev.disabled = true;

    if (prcPrev) {
      prcPrev.addEventListener('click', function () {
        const prevBtn = mainRecContainer.querySelector('button[data-direction="prev"]');
        if (prevBtn) prevBtn.click();
        updatePrcBtnState();
      });
    }

    if (prcNext) {
      prcNext.addEventListener('click', function () {
        const nextBtn = mainRecContainer.querySelector('button[data-direction="next"]');
        if (nextBtn) nextBtn.click();
        updatePrcBtnState();
      });
    }
    function updatePrcBtnState() {
      const prdGridyTrack = prcMainContainer.querySelector('.gridy-track');
      if (!prdGridyTrack || !prdGridyTrack.children.length) return;
      const prcFirstChild = prdGridyTrack.children[0];
      const prcLastChild = prdGridyTrack.lastElementChild;
      const prcActiveChild = Array.from(prdGridyTrack.children).some(child =>
        child.classList.contains('active')
      );
      if (prcFirstChild?.classList.contains('active')) {
        prcPrev.disabled = true;
      } else if (prcActiveChild) {
        prcPrev.disabled = false;
      } else {
        prcPrev.disabled = true;
      }
      if (prcLastChild?.classList.contains('active')) {
        prcNext.disabled = true;
      } else {
        prcNext.disabled = false;
      }
    }
  }

  // Rich Text Truncate Words Functionality
  document.querySelectorAll('.richtext--text-wrapper').forEach(function (wrapper) {
    if (!wrapper) return;

    const btn = wrapper.querySelector('.read-more-btn');
    const truncatedText = wrapper.querySelector('.truncated-text .metafield-rich_text_field');
    const fullText = wrapper.querySelector('.full-text');

    if (btn && truncatedText && fullText) {
      btn.addEventListener('click', function () {
        truncatedText.classList.toggle('hidden');
        fullText.classList.toggle('hidden');
        // Check if theme has button_label variable, otherwise use hardcoded text
        const readMoreLabel = t('buttons.read_more', 'Read More');
        btn.textContent = truncatedText.classList.contains('hidden')
          ? t('buttons.read_less', 'Read Less')
          : readMoreLabel;
      });
    }
  });
});

// Community Slider Section
let marqContent = document.querySelector('.marq-content');
if (marqContent) {
  let comSecHeading = document.querySelector('.slider-heading');

  if (marqContent.childElementCount > 1) {
    comSecHeading.style.display = 'block';
  } else {
    comSecHeading.style.display = 'none';
  }
}

// Header JS
document.querySelectorAll('.menu-item.has-megamenu').forEach(item => {
  let timeout;

  item.addEventListener('mouseenter', () => {
    clearTimeout(timeout);
    item.querySelector('.megamenu').style.display = 'block';
  });

  item.addEventListener('mouseleave', () => {
    timeout = setTimeout(() => {
      item.querySelector('.megamenu').style.display = 'none';
    }, 200); // Adjust the delay time if needed
  });
});

// Header Megamenu Function
window.addEventListener('scroll', () => {
  const announcementBar = document.querySelector('.announcements-bar')?.parentElement;
  const header = document.querySelector('.header-content');
  const megamenus = document.querySelectorAll('.megamenu');

  if (header && megamenus.length > 0 && announcementBar) {
    const headerHeight = header.offsetHeight - 1;

    megamenus.forEach(menu => {
      if (announcementBar.classList.contains('section-in-view')) {
        menu.style.top = 'unset';
      } else {
        menu.style.top = `${headerHeight}px`;
      }
    });
  }
});

// Avoid re-attaching listeners to the same element
function initSlider(cardSlider) {
  if (cardSlider.classList.contains('slider-initialized')) return;
  cardSlider.classList.add('slider-initialized');

  let cardPrev = cardSlider.querySelector('.gridy-arrows button[data-direction="prev"]');
  let cardNext = cardSlider.querySelector('.gridy-arrows button[data-direction="next"]');
  let cardGridy = cardSlider.querySelector('.gridy-track');

  if (!cardPrev || !cardNext || !cardGridy) return;

  cardPrev.style.visibility = 'hidden';

  const updateArrows = () => hidingArrows(cardPrev, cardNext, cardGridy);

  cardPrev.addEventListener('click', updateArrows);
  cardNext.addEventListener('click', updateArrows);
  cardGridy.addEventListener('scroll', updateArrows);
}

// Initial run for already-existing sliders
document.querySelectorAll('.card--product gridy-slider').forEach(initSlider);

// Watch for new sliders added later
const observer = new MutationObserver(mutations => {
  mutations.forEach(mutation => {
    mutation.addedNodes.forEach(node => {
      if (!(node instanceof HTMLElement)) return;

      if (node.matches('.card--product gridy-slider')) {
        initSlider(node);
      } else {
        node.querySelectorAll('.card--product gridy-slider').forEach(initSlider);
      }
    });
  });
});

observer.observe(document.body, { childList: true, subtree: true });

function hidingArrows(cardPrev, cardNext, cardGridy) {
  let cardSliderFirst = cardGridy.firstElementChild;
  let cardSliderLast = cardGridy.lastElementChild;
  if (cardSliderFirst?.classList.contains('active')) {
    cardPrev.style.visibility = 'hidden';
  } else {
    cardPrev.style.visibility = 'visible';
  }
  if (cardSliderLast?.classList.contains('active')) {
    cardNext.style.visibility = 'hidden';
  } else {
    cardNext.style.visibility = 'visible';
  }
}

function goToNext() {
  const fcpSlider = document.querySelector('.fcp--slider-main');
  const fcpSlides = fcpSlider.querySelectorAll('.card');
  let currentIndex = 0;
  if (currentIndex < fcpSlides.length - 1) {
    currentIndex++;
    const slide = fcpSlides[currentIndex];
    if (slide) {
      slide.scrollIntoView({ behavior: 'smooth', inline: 'start' });
    }
  }
}

function goToPrev() {
  const fcpSlider = document.querySelector('.fcp--slider-main');
  const fcpSlides = fcpSlider.querySelectorAll('.card');
  let currentIndex = 0;
  if (currentIndex > 0) {
    currentIndex--;
    const slide = fcpSlides[currentIndex];
    if (slide) {
      slide.scrollIntoView({ behavior: 'smooth', inline: 'start' });
    }
  }
}

document.addEventListener('DOMContentLoaded', function () {
  let currentIndex = 0;
  let fcpSlides;

  // Wait until slides are available
  const waitForSlides = setInterval(() => {
    const fcpSlider = document.querySelector('.fcp--slider-main');
    if (fcpSlider) {
      fcpSlides = fcpSlider.querySelectorAll('.card');
      if (fcpSlides.length > 0) {
        clearInterval(waitForSlides); // Stop checking once found

        // Attach functions only after slides are available
        window.goToNext = function () {
          if (currentIndex < fcpSlides.length - 1) {
            currentIndex++;
            scrollToSlide(currentIndex);
          }
        };

        window.goToPrev = function () {
          if (currentIndex > 0) {
            currentIndex--;
            scrollToSlide(currentIndex);
          }
        };

        function scrollToSlide(index) {
          const slide = fcpSlides[currentIndex];
          if (slide) {
            slide.scrollIntoView({ behavior: 'smooth', inline: 'start' });
          }
        }
      }
    }
  }, 100); // Check every 100ms
});

// Cart Drawer Checkout Functionality
/**
 * Enables/disables the checkout button in the cart drawer based on the terms checkbox.
 * Side effect: Mutates DOM to disable/enable checkout button.
 */
function initCheckboxToggle() {
  const checkbox = document.getElementById('term-checkbox');
  const checkoutBtn = document.getElementById('checkout-btn');
  const validationMessage = document.getElementById('terms-validation-message');

  if (checkbox && checkoutBtn) {
    // Set initial disabled state
    checkoutBtn.disabled = false;
    if (!checkbox.checked) {
      checkoutBtn.classList.add('checkout-btn-disabled');
    }
    
    checkbox.addEventListener('change', function () {
      console.log('checkbox changed', this.checked);
      if (this.checked) {
        checkoutBtn.classList.remove('checkout-btn-disabled');
        if (validationMessage) {
          validationMessage.classList.remove('show');
        }
      } else {
        checkoutBtn.classList.add('checkout-btn-disabled');
      }
    });
    
    checkoutBtn.addEventListener('click', function (e) {
      console.log('checkoutBtn clicked, checkbox checked:', checkbox.checked);
      if (checkbox && !checkbox.checked) {
        e.preventDefault();
        e.stopPropagation();
        console.log('Preventing checkout, showing validation message');
        if (validationMessage) {
          validationMessage.classList.add('show');
          setTimeout(() => {
            validationMessage.classList.remove('show');
          }, 4000);
        }
        return false;
      }
    });
  } else if (checkoutBtn && !checkbox) {
    checkoutBtn.disabled = false;
    checkoutBtn.classList.remove('checkout-btn-disabled');
  }
}

initCheckboxToggle();

const cartDrawer = document.querySelector('.form-elements');

if (cartDrawer) {
  const observer = new MutationObserver(() => {
    initCheckboxToggle();
  });
  observer.observe(cartDrawer, { childList: true, subtree: true });
}

//Closing toggle for filters
document.addEventListener('DOMContentLoaded', function () {
  const detailElements = document.querySelectorAll('.filter-form details');

  detailElements.forEach(details => {
    details.addEventListener('toggle', function () {
      if (details.open) {
        detailElements.forEach(otherDetails => {
          if (otherDetails !== details) {
            otherDetails.removeAttribute('open');
          }
        });
      }
    });
  });
});

// ---------------
// Quick Add to Cart Custom Element
// ---------------
if (!customElements.get('quick-add-to-cart')) {
  customElements.define(
    'quick-add-to-cart',
    class QuickAddToCart extends HTMLElement {
      constructor() {
        super();
        this.variantId = this.getAttribute('data-variant-id');
        this.button = this;
        this.loading = false;
        this.handleClick = this.handleClick.bind(this);
      }

      /**
       * Sets up button role, tabIndex, and event listeners for accessibility and interaction.
       */
      connectedCallback() {
        if (!this.button.hasAttribute('role')) {
          this.button.setAttribute('role', 'button');
        }
        this.button.tabIndex = 0;
        this.button.addEventListener('click', this.handleClick);
        this.button.addEventListener('keydown', e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.handleClick();
          }
        });
      }

      /**
       * Cleans up event listeners when element is removed from the DOM.
       */
      disconnectedCallback() {
        this.button.removeEventListener('click', this.handleClick);
      }

      /**
       * Updates the button UI state to reflect loading or ready status.
       * @param {boolean} isLoading
       */
      setLoading(isLoading) {
        this.loading = isLoading;
        if (isLoading) {
          this.button.classList.add('loading');
          this.button.setAttribute('aria-disabled', 'true');
          this.button.textContent = t('cart.adding', 'Adding...');
        } else {
          this.button.classList.remove('loading');
          this.button.removeAttribute('aria-disabled');
          this.button.textContent = t('cart.add_to_cart', 'Add to cart');
        }
      }

      /**
       * Handles the quick add to cart button click event.
       * Sends an AJAX request to add the product variant to the cart and updates UI accordingly.
       * Side effects: Mutates button state, dispatches 'ajaxProduct:added' event, updates cart drawer.
       */
      async handleClick() {
        if (this.loading) return;
        if (!this.variantId) return;
        this.setLoading(true);
        try {
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify({ id: this.variantId, quantity: 1 }),
          });
          const json = await res.json();
          if (!res.ok) throw new Error(json.description || 'Error adding to cart');
          // Dispatch event to update cart drawer
          document.dispatchEvent(new CustomEvent('ajaxProduct:added'));
          this.setLoading(false);
          // Optionally, show a success state
          this.button.textContent = t('cart.added', 'Added');
          setTimeout(() => {
            this.button.textContent = t('cart.add_to_cart', 'Add to cart');
          }, 1200);
        } catch (err) {
          this.setLoading(false);
          this.button.textContent = t('cart.error', 'Error');
          setTimeout(() => {
            this.button.textContent = t('cart.add_to_cart', 'Add to cart');
          }, 2000);
        }
      }
    }
  );
}

// ---------------
// tier-discounts Progress Bar Component
// ---------------
if (!customElements.get('tier-discounts-progress')) {
  customElements.define(
    'tier-discounts-progress',
    class TierDiscountsProgress extends HTMLElement {
      constructor() {
        super();
        this.currentAmount = 0;
        this.currency = this.getAttribute('data-currency') || 'DKK';
        this.initThresholds();
        this.translations = this.getTranslations();
        this.updateTimeout = null;
        this.isUpdating = false;
        
        // Create unique identifier for this instance
        this.instanceId = Math.random().toString(36).substr(2, 9);
        
        // Bind methods once
        this.boundHandleCartUpdate = this.handleCartUpdate.bind(this);
        this.boundHandleProductAdded = this.handleProductAdded.bind(this);
      }

      getTranslations() {
        const translationsEl = this.parentElement.querySelector('[data-tier-discounts-translations]');
        if (translationsEl) {
          return JSON.parse(translationsEl.textContent);
        }
        return {
          subtitle_html: 'Spend <span class="amount-needed">[[amount]]</span> more to get [[discount]]% off',
          reached_html: 'Congratulations! You have reached [[discount]]% discount!',
          reached_and_next_tier_html: 'Congratulations! You have reached [[current_discount]]% discount. Spend only [[amount]] more to get [[next_discount]]% discount.'
        };
      }

      initThresholds() {
        const currencyRate = (window?.Shopify?.currency?.rate && parseFloat(window.Shopify.currency.rate) > 0) 
          ? parseFloat(window.Shopify.currency.rate)
          : 1;
        this.thresholds = [
          {
            amount: (parseInt(this.getAttribute('data-threshold-1')) || 0) * currencyRate,
            discount: parseInt(this.getAttribute('data-discount-1')) || 10
          },
          {
            amount: (parseInt(this.getAttribute('data-threshold-2')) || 99900) * currencyRate,
            discount: parseInt(this.getAttribute('data-discount-2')) || 20
          },
          {
            amount: (parseInt(this.getAttribute('data-threshold-3')) || 199900) * currencyRate,
            discount: parseInt(this.getAttribute('data-discount-3')) || 25
          }
        ];
      }

      connectedCallback() {
        // Use global event manager to prevent multiple listeners for same events
        if (!window.tierDiscountProgressInstances) {
          window.tierDiscountProgressInstances = new Set();
          window.tierDiscountEventHandlers = {
            initialized: false,
            instances: new Set()
          };
        }
        
        window.tierDiscountProgressInstances.add(this);
        window.tierDiscountEventHandlers.instances.add(this);
        this.initThresholds()
        
        // Only initialize global listeners once
        if (!window.tierDiscountEventHandlers.initialized) {
          document.addEventListener('cart:update', this.globalCartUpdateHandler.bind(this));
          document.addEventListener('ajaxProduct:added', this.globalProductAddedHandler.bind(this));
          document.addEventListener('cart:calculators:update', this.globalCartUpdateHandler.bind(this));
          window.tierDiscountEventHandlers.initialized = true;
          
          if (this.ENABLE_LOGS || ENABLE_LOGS) {
            console.log('[TIER-PROGRESS] Global event handlers initialized');
          }
        }
        
        this.updateFromCart();
      }
      
      globalCartUpdateHandler(event) {
        // No debouncing for cart updates - should be instant like the original
        if (this.ENABLE_LOGS || ENABLE_LOGS) {
          console.log('[TIER-PROGRESS] Global cart update, instances count:', window.tierDiscountEventHandlers.instances.size);
        }
        
        window.tierDiscountEventHandlers.instances.forEach(instance => {
          if (instance.isConnected) {
            // Call updateProgress directly for instant response
            if (event.detail && event.detail.original_total_price !== undefined) {
              instance.currentAmount = event.detail.original_total_price;
            } else if (event.detail && event.detail.total_price !== undefined) {
              instance.currentAmount = event.detail.total_price;
            } else {
              instance.updateFromCart();
              return;
            }
            
            if (this.ENABLE_LOGS || ENABLE_LOGS) {
              console.log(`[TIER-PROGRESS-${instance.instanceId}] Event update - using amount:`, instance.currentAmount);
            }
            instance.updateProgress();
          }
        });
      }
      
      globalProductAddedHandler(event) {
        // Debounce and update all instances efficiently
        if (this.globalProductTimeout) {
          clearTimeout(this.globalProductTimeout);
        }
        
        this.globalProductTimeout = setTimeout(() => {
          if (this.ENABLE_LOGS || ENABLE_LOGS) {
            console.log('[TIER-PROGRESS] Global product added, instances count:', window.tierDiscountEventHandlers.instances.size);
          }
          
          window.tierDiscountEventHandlers.instances.forEach(instance => {
            if (instance.isConnected) {
              // Call updateFromCart directly to avoid double debouncing
              instance.updateFromCart();
            }
          });
          this.globalProductTimeout = null;
        }, 100); // Faster response time
      }

      disconnectedCallback() {
        if (window.tierDiscountProgressInstances) {
          window.tierDiscountProgressInstances.delete(this);
          window.tierDiscountEventHandlers.instances.delete(this);
        }
        
        // Clear any pending timeouts for this instance
        if (this.updateTimeout) {
          clearTimeout(this.updateTimeout);
        }
      }

      async updateFromCart() {
        // Prevent multiple simultaneous updates from same instance
        if (this.isUpdating) {
          if (this.ENABLE_LOGS || ENABLE_LOGS) {
            console.log(`[TIER-PROGRESS-${this.instanceId}] Update already in progress, skipping`);
          }
          return;
        }
        
        this.isUpdating = true;
        
        try {
          const response = await fetch('/cart.js');
          const cart = await response.json();
          
          // Use original_total_price (before discounts) instead of total_price (after discounts)
          this.currentAmount = cart.original_total_price || cart.total_price;
          
          if (this.ENABLE_LOGS || ENABLE_LOGS) {
            console.log(`[TIER-PROGRESS-${this.instanceId}] Cart data:`, {
              total_price: cart.total_price,
              original_total_price: cart.original_total_price,
              using_amount: this.currentAmount
            });
          }
          
          this.updateProgress();
        } catch (error) {
          console.error(`[TIER-PROGRESS-${this.instanceId}] Error fetching cart:`, error);
        } finally {
          this.isUpdating = false;
        }
      }

      handleCartUpdate(event) {
        // These methods are now called directly by global handlers
        // This is kept for direct instance calls if needed
        if (event.detail && event.detail.original_total_price !== undefined) {
          this.currentAmount = event.detail.original_total_price;
        } else if (event.detail && event.detail.total_price !== undefined) {
          this.currentAmount = event.detail.total_price;
        } else {
          this.updateFromCart();
          return;
        }
        
        if (this.ENABLE_LOGS || ENABLE_LOGS) {
          console.log(`[TIER-PROGRESS-${this.instanceId}] Direct event update - using amount:`, this.currentAmount);
        }
        this.updateProgress();
      }

      handleProductAdded() {
        // This method is now called directly by global handlers
        // This is kept for direct instance calls if needed
        this.updateFromCart();
      }
      getCurrentTier(amount) {
        // Get tier settings from data attributes with currency conversion
        const currencyRate = (window?.Shopify?.currency?.rate && parseFloat(window.Shopify.currency.rate) > 0) 
          ? parseFloat(window.Shopify.currency.rate)
          : 1;
          
        const threshold1 = (parseInt(this.dataset['threshold-1']) || 0) * currencyRate;
        const discount1 = parseInt(this.dataset['discount-1']) || 10;
        const threshold2 = (parseInt(this.dataset['threshold-2']) || 99900) * currencyRate;
        const discount2 = parseInt(this.dataset['discount-2']) || 20;
        const threshold3 = (parseInt(this.dataset['threshold-3']) || 199900) * currencyRate;
        const discount3 = parseInt(this.dataset['discount-3']) || 25;

        // Show what discount they're working toward next
        if (amount >= threshold3) { // Highest tier reached
          return {
            current: { amount: threshold3, discount: discount3 },
            next: null,
            tierIndex: 2
          };
        } else if (amount >= threshold2) { // Middle tier reached, working toward highest
          return {
            current: { amount: threshold2, discount: discount2 },
            next: { amount: threshold3, discount: discount3 },
            tierIndex: 1
          };
        } else { // Lowest tier, working toward middle
          return {
            current: { amount: 0, discount: discount1 },
            next: { amount: threshold2, discount: discount2 },
            tierIndex: 0
          };
        }
      }

      updateProgress() {
        const progressElement = this.querySelector('.tier-discounts-progress');
        const subtitleElement = this.querySelector('.tier-discounts-subtitle');
        const scaleElement = this.querySelector('.tier-discounts-scale');
        
        if (!progressElement || !subtitleElement) return;

        const shopCurrency = window?.Shopify?.currency?.active || this.currency;
        let currentAmountInBase = this.currentAmount;

        if (shopCurrency !== this.currency && window?.Shopify?.currency?.rate && window.Shopify.currency.rate > 0) {
          // Convert current amount to base currency (DKK) for tier calculation
          currentAmountInBase = this.currentAmount;
        }

        const tierInfo = this.getCurrentTier(currentAmountInBase);
        
        // Always use highest tier from settings as target for percentage calculation
        const HIGHEST_TIER_AMOUNT = (parseInt(this.getAttribute('data-threshold-3')) || 199900) * parseFloat(window.Shopify.currency.rate);
        let targetAmountForPercentage = HIGHEST_TIER_AMOUNT;
        
        // Calculate percentage using same currency amounts
        let percentage = Math.min((currentAmountInBase / targetAmountForPercentage) * 100, 100);
        
        if (this.ENABLE_LOGS || ENABLE_LOGS) {
          console.log(`[TIER-PROGRESS-${this.instanceId}] 🚀 Tier Discounts Debug:`, {
            currentAmount: this.currentAmount,
            currentAmountInBase: currentAmountInBase,
            currentTier: tierInfo.current?.discount || 'none',
            nextTier: tierInfo.next?.discount || 'max reached',
            highestTierAmount: HIGHEST_TIER_AMOUNT,
            targetForPercentage: targetAmountForPercentage,
            calculatedPercentage: percentage.toFixed(1) + '%',
            shopCurrency,
            displayCurrency: this.currency,
            currencyRate: window?.Shopify?.currency?.rate
          });
        }
        
        let message = '';

        if (!this.translations) {
          console.error('Translations not initialized.');
          return;
        }

        if (tierInfo.current && !tierInfo.next) {
          // Maximum tier reached
          percentage = 100;
          message = this.translations.reached_html.replace('[[discount]]', tierInfo.current.discount);
          progressElement.classList.add('completed');
          progressElement.classList.remove('in-progress');
        } else if (tierInfo.next) {
          // Show progress to next tier
          let nextTierAmount = tierInfo.next.amount;
          
          // Handle currency conversion for display amount only
          if (shopCurrency !== this.currency && window?.Shopify?.currency?.rate && window.Shopify.currency.rate > 0) {
            nextTierAmount = nextTierAmount * window.Shopify.currency.rate;
          }
          
          let amountNeeded = nextTierAmount - this.currentAmount;
          
          if (amountNeeded > 0) {
            message = this.translations.reached_and_next_tier_html
              .replace('[[current_discount]]', tierInfo.current.discount)
              .replace('[[amount]]', this.formatMoney(amountNeeded))
              .replace('[[next_discount]]', tierInfo.next.discount);
            progressElement.classList.remove('completed');
            progressElement.classList.add('in-progress');
          } else {
            message = this.translations.reached_html.replace('[[discount]]', tierInfo.next.discount);
            progressElement.classList.add('completed');
            progressElement.classList.remove('in-progress');
          }
        }

        progressElement.style.width = `${percentage}%`;
        
        if (this.ENABLE_LOGS || ENABLE_LOGS) {
          console.log(`[TIER-PROGRESS-${this.instanceId}] Progress updated:`, {
            percentage: percentage.toFixed(1) + '%',
            width: progressElement.style.width
          });
        }
        
        subtitleElement.innerHTML = message;

        // Update scale to show discount percentages with achieved states
        if (scaleElement) {
          const tier1Class = tierInfo.tierIndex >= 0 ? 'achieved' : '';
          const tier2Class = tierInfo.tierIndex >= 1 ? 'achieved' : '';
          const tier3Class = tierInfo.tierIndex >= 2 ? 'achieved' : '';
          
          const discount1 = parseInt(this.getAttribute('data-discount-1')) || 10;
          const discount2 = parseInt(this.getAttribute('data-discount-2')) || 20;
          const discount3 = parseInt(this.getAttribute('data-discount-3')) || 25;
          
          scaleElement.innerHTML = `
            <span class="tier-1 ${tier1Class}">${discount1}%</span>
            <span class="tier-2 ${tier2Class}">${discount2}%</span>
            <span class="tier-3 ${tier3Class}">${discount3}%</span>
          `;
        }
      }

      formatMoney(amount) {
        // Check if Woolman utils is available
        if (typeof Woolman !== 'undefined' && 
            typeof Woolman.Utils !== 'undefined' && 
            typeof Woolman.Utils.formatMoney === 'function') {
          try {
            const moneyFormat = (typeof theme !== 'undefined' && theme.settings && theme.settings.moneyFormat) 
              ? theme.settings.moneyFormat 
              : '{{amount_with_comma_separator}} kr.';
            return Woolman.Utils.formatMoney(amount, moneyFormat);
          } catch (error) {
            console.warn('Error formatting money with Woolman.Utils:', error);
          }
        }
        
        // Fallback formatting
        const formatted = (amount / 100).toFixed(2).replace('.', ',');
        return `${formatted} kr.`;
      }
    }
  );
}



// ---------------
// Prevent email scraping
// ---------------
document.addEventListener("DOMContentLoaded", function () {
  const links = document.querySelectorAll('a[href^="mailto:"]');

  links.forEach(link => {
    const email = link.getAttribute("href").replace("mailto:", "");

    // Basic obfuscation
    const [user, domain] = email.split("@");

    // Clear the email to bots before JS finishes
    link.setAttribute("href", "#");
    link.textContent = "[email protected]";

    // Reapply real email with JS (only visible to users)
    setTimeout(() => {
      const safeEmail = `${user}@${domain}`;
      link.setAttribute("href", `mailto:${safeEmail}`);
      link.textContent = safeEmail;
    }, 50); // Small delay helps avoid some bots scraping before JS runs
  });
});

function isHeadless() {
  return (
    navigator.webdriver ||                          // true in headless
    /HeadlessChrome/.test(navigator.userAgent) ||   // common userAgent
    !navigator.plugins.length ||                    // no plugins
    !navigator.languages ||                         // missing lang info
    screen.width === 0 || screen.height === 0       // virtual screens
  );
}

if (isHeadless()) {
  // Option 1: redirect
  // window.location.href = "/blocked";
  
  // Option 2: disable email links
  document.querySelectorAll('a[href^="mailto:"]').forEach(el => {
    el.removeAttribute("href");
    el.textContent = "Blocked for bots";
  });
}

// ---------------
// tier-discounts Price Component - Integrated Version
// ---------------
if (!customElements.get('tier-discounts-price')) {
  customElements.define(
    'tier-discounts-price',
    class TierDiscountsPrice extends HTMLElement {
      constructor() {
        super();
        this.originalPrice = parseFloat(this.getAttribute('data-price')) || 0;
        this.compareAtPrice = parseFloat(this.getAttribute('data-compare-at-price')) || 0;
        this.currentAmount = 0;
        this.lastCalculatedDiscount = 0;
        this.lastVariantPrice = null;
        this.lastFramePrice = null;
        this.initThresholds();
        // Cache DOM elements for better performance
        this.domElements = null;
      }

      initThresholds() {
        // Safe currency rate access with fallback to 1 if not available
        const currencyRate = (window?.Shopify?.currency?.rate && window.Shopify.currency.rate > 0) 
          ? window.Shopify.currency.rate 
          : 1;
        
        this.thresholds = [
          {
            amount: (parseInt(this.getAttribute('data-threshold-1')) || 0) * currencyRate,
            discount: parseInt(this.getAttribute('data-discount-1')) || 10
          },
          {
            amount: (parseInt(this.getAttribute('data-threshold-2')) || 99900) * currencyRate,
            discount: parseInt(this.getAttribute('data-discount-2')) || 15
          },
          {
            amount: (parseInt(this.getAttribute('data-threshold-3')) || 199900) * currencyRate,
            discount: parseInt(this.getAttribute('data-discount-3')) || 20
          }
        ];
        
        if (ENABLE_LOGS) {
          console.log('[TIER-PRICE] Initialized with thresholds:', this.thresholds);
        }
      }

      connectedCallback() {
        if (ENABLE_LOGS) {
          console.log('[TIER-PRICE] Component connected, original price:', this.originalPrice);
        }

        // Listen for cart events
        this.addEventListener('cart:update', this.handleCartUpdate.bind(this));
        document.addEventListener('ajaxProduct:added', this.handleProductAdded.bind(this));
        document.addEventListener('cart:calculators:update', this.handleCartUpdate.bind(this));
        
        // Listen for variant changes - use the same event your existing system uses
        document.addEventListener('variant:changed', this.handleVariantChanged.bind(this));
        
        // Initial cart fetch
        this.updateFromCart();
        
        // Store reference to original updatePrices function if it exists
        if (typeof window.updatePrices === 'function') {
          this.originalUpdatePrices = window.updatePrices;
          // Replace with our enhanced version
          window.updatePrices = this.enhancedUpdatePrices.bind(this);
          if (ENABLE_LOGS) {
            console.log('[TIER-PRICE] Replaced window.updatePrices with enhanced version');
          }
        } else {
          if (ENABLE_LOGS) {
            console.log('[TIER-PRICE] No window.updatePrices function found, will handle updates manually');
          }
        }
      }

      disconnectedCallback() {
        if (ENABLE_LOGS) {
          console.log('[TIER-PRICE] Component disconnected');
        }
        
        this.removeEventListener('cart:update', this.handleCartUpdate.bind(this));
        document.removeEventListener('ajaxProduct:added', this.handleProductAdded.bind(this));
        document.removeEventListener('cart:calculators:update', this.handleCartUpdate.bind(this));
        document.removeEventListener('variant:changed', this.handleVariantChanged.bind(this));
        
        // Restore original updatePrices if we replaced it
        if (this.originalUpdatePrices) {
          window.updatePrices = this.originalUpdatePrices;
        }
      }

      // Enhanced version of updatePrices that includes tier discount logic
      enhancedUpdatePrices(price, framePrice = 0, comparePrice = null, frameComparePrice = 0) {
        if (ENABLE_LOGS) {
          console.log('[TIER-PRICE] enhancedUpdatePrices called:', {
            price, framePrice, comparePrice, frameComparePrice,
            originalUpdatePricesExists: !!this.originalUpdatePrices
          });
        }
        
        // Call the original updatePrices function first
        if (this.originalUpdatePrices) {
          this.originalUpdatePrices(price, framePrice, comparePrice, frameComparePrice);
        }
        
        // Update this component's price if this is the right product
        const effectivePrice = price || window.priceState?.variantPrice || this.originalPrice;
        const effectiveFramePrice = framePrice !== undefined ? framePrice : (window.priceState?.framePrice || 0);
        
        // Calculate total price including frame
        const totalPrice = parseFloat(effectivePrice) + parseFloat(effectiveFramePrice);
        
        // Update this component's displayed price based on tier discount
        this.updateTierDiscountDisplay(totalPrice, effectiveFramePrice);
      }

      async updateFromCart() {
        try {
          const response = await fetch('/cart.js');
          const cart = await response.json();
          
          this.currentAmount = cart.original_total_price || cart.total_price || 0;
          
          if (ENABLE_LOGS) {
            console.log('[TIER-PRICE] Cart updated:', {
              originalTotalPrice: cart.original_total_price,
              totalPrice: cart.total_price,
              usingAmount: this.currentAmount,
              itemCount: cart.item_count
            });
          }
          
          // Trigger a price update with current variant data
          this.updateTierDiscountDisplay();
          
        } catch (error) {
          console.error('[TIER-PRICE] Error fetching cart:', error);
        }
      }

      handleCartUpdate(event) {
        if (ENABLE_LOGS) {
          console.log('[TIER-PRICE] Cart update event:', event.detail);
        }
        
        if (event.detail && event.detail.original_total_price !== undefined) {
          this.currentAmount = event.detail.original_total_price;
        } else if (event.detail && event.detail.total_price !== undefined) {
          this.currentAmount = event.detail.total_price;
        } else {
          this.updateFromCart();
          return;
        }
        
        this.updateTierDiscountDisplay();
      }

      handleProductAdded() {
        if (ENABLE_LOGS) {
          console.log('[TIER-PRICE] Product added, updating cart');
        }
        setTimeout(() => {
          this.updateFromCart();
        }, 300);
      }

      handleVariantChanged(event) {
        if (ENABLE_LOGS) {
          console.log('[TIER-PRICE] Variant changed event:', event.detail);
        }
        
        // Update the original price when variant changes
        if (event.detail && event.detail.price !== undefined) {
          this.originalPrice = parseFloat(event.detail.price);
          this.compareAtPrice = parseFloat(event.detail.comparePrice || 0);
          
          // Update the data attributes
          this.setAttribute('data-price', this.originalPrice);
          this.setAttribute('data-compare-at-price', this.compareAtPrice);
          
          // Check if the existing updatePrices system is being called
          // If not, we need to trigger the display update ourselves
          setTimeout(() => {
            if (ENABLE_LOGS) {
              console.log('[TIER-PRICE] Checking if updatePrices was called after variant change');
            }
            // Trigger our own price update with the new variant price
            this.updateTierDiscountDisplay(this.originalPrice);
          }, 40);
        }
      }

      getCurrentTier(amount) {
        // Sort thresholds in descending order to check highest first
        const sortedThresholds = [...this.thresholds].sort((a, b) => b.amount - a.amount);
        
        for (const threshold of sortedThresholds) {
          if (amount >= threshold.amount) {
            return { discount: threshold.discount, threshold: threshold.amount };
          }
        }
        
        return { discount: 0, threshold: 0 };
      }

      formatPrice(priceInCents) {
        // Use the same formatting as your existing system
        if (typeof Woolman !== 'undefined' && 
            typeof Woolman.Utils !== 'undefined' && 
            typeof Woolman.Utils.formatMoney === 'function') {
          try {
            const moneyFormat = (typeof theme !== 'undefined' && theme.settings && theme.settings.moneyFormat) 
              ? theme.settings.moneyFormat 
              : '{{amount_with_comma_separator}} kr.';
            return Woolman.Utils.formatMoney(priceInCents, moneyFormat);
          } catch (error) {
            console.warn('[TIER-PRICE] Error with Woolman formatting:', error);
          }
        }
        
        // Fallback to Shopify formatting
        if (window.Shopify && window.Shopify.formatMoney && window.Shopify.money_format) {
          return window.Shopify.formatMoney(priceInCents, window.Shopify.money_format);
        }
        
        // Final fallback
        const price = priceInCents / 100;
        return new Intl.NumberFormat('da-DK', {
          style: 'currency',
          currency: 'DKK',
          minimumFractionDigits: price % 1 === 0 ? 0 : 2
        }).format(price);
      }

      updateTierDiscountDisplay(variantPrice = null, framePrice = null) {
        // Get current prices from various sources
        const currentVariantPrice = variantPrice || window.priceState?.variantPrice || this.originalPrice;
        const currentFramePrice = framePrice !== null ? framePrice : (window.frameSelection?.framePrice || 0);
        const totalProductPrice = parseFloat(currentVariantPrice) + parseFloat(currentFramePrice);
        
        // Calculate tier discount based on cart amount
        const tierInfo = this.getCurrentTier(this.currentAmount);
        const discountPercentage = tierInfo.discount;
        
        // Performance optimization: only update DOM if something actually changed
        const priceChanged = (this.lastVariantPrice !== currentVariantPrice || this.lastFramePrice !== currentFramePrice);
        const discountChanged = (this.lastCalculatedDiscount !== discountPercentage);
        
        if (!priceChanged && !discountChanged) {
          if (ENABLE_LOGS) {
            console.log('[TIER-PRICE] No changes, skipping DOM update');
          }
          return;
        }
        
        this.lastCalculatedDiscount = discountPercentage;
        this.lastVariantPrice = currentVariantPrice;
        this.lastFramePrice = currentFramePrice;
        
        // Calculate final prices with tier discount
        let finalPrice, finalComparePrice;
        if (discountPercentage > 0) {
          finalPrice = totalProductPrice * (1 - discountPercentage / 100);
          finalComparePrice = totalProductPrice; // Original price becomes compare price
        } else {
          finalPrice = totalProductPrice;
          finalComparePrice = null;
        }
        
        // Update this component's display
        this.updateComponentDisplay(finalPrice, finalComparePrice, discountPercentage);
        
        // Update ALL price displays on the page (like original updatePrices does)
        this.updateAllPriceDisplays(finalPrice, finalComparePrice, currentVariantPrice, currentFramePrice, discountPercentage);
      }

      updateComponentDisplay(finalPrice, finalComparePrice, discountPercentage) {
        // Cache DOM elements for better performance
        if (!this.domElements) {
          this.domElements = {
            priceRegular: this.querySelector('.price__regular'),
            priceSale: this.querySelector('.price__sale'),
            regularPriceItem: this.querySelector('.price__regular .price-item--regular'),
            salePriceItem: this.querySelector('.price__sale .price-item--sale'),
            compareAtPriceItem: this.querySelector('.price__sale .price-item--regular'),
            badgeElement: this.querySelector('.badge.tier-discount'),
            discountPercentageElement: this.querySelector('.discount-percentage')
          };
        }

        // Get elements from cache with fallback safety
        const {
          priceRegular,
          priceSale,
          regularPriceItem,
          salePriceItem,
          compareAtPriceItem,
          badgeElement,
          discountPercentageElement
        } = this.domElements;

        if (!priceRegular || !priceSale || !regularPriceItem) return;

        if (discountPercentage > 0) {
          // Show as sale price
          priceRegular.style.display = 'none';
          priceSale.style.display = 'block';
          
          if (compareAtPriceItem) {
            compareAtPriceItem.textContent = this.formatPrice(finalComparePrice);
          }
          if (salePriceItem) {
            salePriceItem.textContent = this.formatPrice(finalPrice);
          }

          // Show tier discount badge
          if (badgeElement && discountPercentageElement) {
            badgeElement.style.display = 'inline-flex';
            discountPercentageElement.textContent = discountPercentage;
          }

          this.classList.add('price--on-sale');
          
          if (ENABLE_LOGS) {
            console.log('[TIER-PRICE] Applied discount to component:', {
              original: this.formatPrice(finalComparePrice),
              discounted: this.formatPrice(finalPrice),
              discount: discountPercentage + '%'
            });
          }
        } else {
          // Show regular price
          priceRegular.style.display = 'block';
          priceSale.style.display = 'none';
          regularPriceItem.textContent = this.formatPrice(finalPrice);
          
          if (badgeElement) {
            badgeElement.style.display = 'none';
          }

          this.classList.remove('price--on-sale');
          
          if (ENABLE_LOGS) {
            console.log('[TIER-PRICE] No discount, showing regular price on component:', this.formatPrice(finalPrice));
          }
        }
      }

      updateAllPriceDisplays(finalPrice, finalComparePrice, variantPrice, _framePrice, discountPercentage) {
        if (ENABLE_LOGS) {
          console.log('[TIER-PRICE] Updating all price displays on page:', {
            finalPrice: this.formatPrice(finalPrice),
            finalComparePrice: finalComparePrice ? this.formatPrice(finalComparePrice) : null,
            discountPercentage: discountPercentage + '%'
          });
        }
        
        // Update main add to cart button price (.total-price)
        const mainAtc = document.querySelector('.product-form__submit');
        if (mainAtc) {
          const mainPriceContainer = mainAtc.querySelector('.total-price');
          if (mainPriceContainer) {
            mainPriceContainer.textContent = this.formatPrice(finalPrice);
            if (ENABLE_LOGS) {
              console.log('[TIER-PRICE] Updated main ATC button price');
            }
          }

          // Update compare price in main button if exists
          const mainCompareContainer = mainAtc.querySelector('.total-compare-price');
          if (mainCompareContainer) {
            if (finalComparePrice) {
              mainCompareContainer.textContent = this.formatPrice(finalComparePrice);
              mainCompareContainer.style.display = 'block';
            } else {
              mainCompareContainer.style.display = 'none';
            }
          }
        }

        // Update main product price (.price-item in .price__regular)
        const mainPrdPrice = document.querySelector('.price__regular .price-item');
        if (mainPrdPrice && discountPercentage === 0) {
          // Only update if no discount (regular price display)
          mainPrdPrice.textContent = this.formatPrice(finalPrice);
          if (ENABLE_LOGS) {
            console.log('[TIER-PRICE] Updated main product price');
          }
        }

        // Update buy bar price (.the-buy-bar .price-item)
        const buyBarPrice = document.querySelector('.the-buy-bar .price-item');
        if (buyBarPrice && discountPercentage === 0) {
          // Only update if no discount since buy bar uses tier-discounts-price component
          buyBarPrice.textContent = this.formatPrice(finalPrice);
          if (ENABLE_LOGS) {
            console.log('[TIER-PRICE] Updated buy bar price');
          }
        }

        // Update custom button price (.custom-btn .total-price)
        const customBtn = document.querySelector('.product-form__buttons .custom-btn');
        if (customBtn) {
          const customPriceContainer = customBtn.querySelector('.total-price');
          if (customPriceContainer) {
            customPriceContainer.textContent = this.formatPrice(finalPrice);
            if (ENABLE_LOGS) {
              console.log('[TIER-PRICE] Updated custom button price');
            }
          }

          // Update compare price in custom button if exists
          const customCompareContainer = customBtn.querySelector('.total-compare-price');
          if (customCompareContainer) {
            if (finalComparePrice) {
              customCompareContainer.textContent = this.formatPrice(finalComparePrice);
              customCompareContainer.style.display = 'block';
            } else {
              customCompareContainer.style.display = 'none';
            }
          }
        }

        // Update buy button bar button-check-option price
        const buyBarCustomBtn = document.querySelector('.the-buy-bar #button-check-option');
        if (buyBarCustomBtn) {
          const buyBarCustomPriceContainer = buyBarCustomBtn.querySelector('.total-price');
          if (buyBarCustomPriceContainer) {
            buyBarCustomPriceContainer.textContent = this.formatPrice(finalPrice);
            if (ENABLE_LOGS) {
              console.log('[TIER-PRICE] Updated buy bar custom button price');
            }
          }

          // Update compare price in buy bar custom button if exists
          const buyBarCustomCompareContainer = buyBarCustomBtn.querySelector('.total-compare-price');
          if (buyBarCustomCompareContainer) {
            if (finalComparePrice) {
              buyBarCustomCompareContainer.textContent = this.formatPrice(finalComparePrice);
              buyBarCustomCompareContainer.style.display = 'inline';
              buyBarCustomCompareContainer.style.textDecoration = 'line-through';
              buyBarCustomCompareContainer.style.marginLeft = '8px';
              buyBarCustomCompareContainer.style.opacity = '0.7';
            } else {
              buyBarCustomCompareContainer.style.display = 'none';
            }
          }
        }

        // Update size price tag (.size-total-price) - shows variant price only
        const sizePrice = document.querySelector('.size-total-price');
        if (sizePrice) {
          // Calculate tier discount on variant price only (without frame)
          let variantFinalPrice;
          if (discountPercentage > 0) {
            variantFinalPrice = variantPrice * (1 - discountPercentage / 100);
          } else {
            variantFinalPrice = variantPrice;
          }
          
          sizePrice.innerHTML = ` + ${this.formatPrice(variantFinalPrice)}`;
          if (ENABLE_LOGS) {
            console.log('[TIER-PRICE] Updated size price tag');
          }
        }

        // Update all other tier-discounts-price components on the page
        const allTierComponents = document.querySelectorAll('tier-discounts-price');
        allTierComponents.forEach(component => {
          if (component !== this) {
            // Update the data attributes
            component.setAttribute('data-price', variantPrice);
            // Trigger their own update with the same cart amount
            if (typeof component.updateTierDiscountDisplay === 'function') {
              component.originalPrice = parseFloat(variantPrice);
              component.currentAmount = this.currentAmount;
              component.updateComponentDisplay(finalPrice, finalComparePrice, discountPercentage);
            }
          }
        });

        if (ENABLE_LOGS) {
          console.log('[TIER-PRICE] Finished updating all price displays');
        }
      }
    }
  );
}

// ---------------
// Collection Page Tier Discount Manager
// ---------------
if (!window.CollectionTierDiscountManager) {
  window.CollectionTierDiscountManager = {
    initialized: false,
    
    init() {
      if (this.initialized) return;
      
      if (ENABLE_LOGS) {
        console.log('[COLLECTION-TIER] Initializing collection page tier discount manager');
      }
      
      // Listen for cart events on collection pages
      document.addEventListener('cart:update', this.handleCartUpdate.bind(this));
      document.addEventListener('ajaxProduct:added', this.handleCartUpdate.bind(this));
      document.addEventListener('cart:calculators:update', this.handleCartUpdate.bind(this));
      document.addEventListener('cart:change', this.handleCartUpdate.bind(this));
      
      this.initialized = true;
      
      // Initial update on page load
      setTimeout(() => {
        this.updateAllCollectionPrices();
      }, 100);
    },
    
    async handleCartUpdate(event) {
      if (ENABLE_LOGS) {
        console.log('[COLLECTION-TIER] Cart update event on collection page:', event.detail);
      }
      
      // Debounce updates to prevent multiple rapid calls
      if (this.updateTimeout) {
        clearTimeout(this.updateTimeout);
      }
      
      this.updateTimeout = setTimeout(() => {
        this.updateAllCollectionPrices();
        this.updateAllCollectionBadges();
        this.updateTimeout = null;
      }, 150);
    },
    
    async updateAllCollectionPrices() {
      try {
        // Fetch current cart
        const response = await fetch('/cart.js');
        const cart = await response.json();
        const cartAmount = cart.original_total_price || cart.total_price || 0;
        
        if (ENABLE_LOGS) {
          console.log('[COLLECTION-TIER] Updating collection prices, cart amount:', cartAmount);
        }
        
        // Update all tier-discounts-price components on the page
        const tierComponents = document.querySelectorAll('tier-discounts-price');
        tierComponents.forEach(component => {
          if (typeof component.currentAmount !== 'undefined') {
            component.currentAmount = cartAmount;
            if (typeof component.updateTierDiscountDisplay === 'function') {
              component.updateTierDiscountDisplay();
            }
          }
        });
        
        // Update collection page badges
        this.updateAllCollectionBadges(cartAmount);
        
      } catch (error) {
        console.error('[COLLECTION-TIER] Error updating collection prices:', error);
      }
    },
    
    updateAllCollectionBadges(cartAmount = null) {
      // Get cart amount if not provided
      if (cartAmount === null) {
        fetch('/cart.js')
          .then(response => response.json())
          .then(cart => {
            const amount = cart.original_total_price || cart.total_price || 0;
            this.updateBadgesWithAmount(amount);
          })
          .catch(error => {
            console.error('[COLLECTION-TIER] Error fetching cart for badges:', error);
          });
      } else {
        this.updateBadgesWithAmount(cartAmount);
      }
    },
    
    getThresholdsFromDOM() {
      // Try to find a tier-discounts-price component to read settings from
      const tierComponent = document.querySelector('tier-discounts-price');
      
      if (!tierComponent) {
        if (ENABLE_LOGS) {
          console.log('[COLLECTION-TIER] No tier-discounts-price component found on page');
        }
        return [];
      }
      
      try {
        // Safe currency rate access with fallback to 1 if not available
        const currencyRate = (window?.Shopify?.currency?.rate && window.Shopify.currency.rate > 0) 
          ? window.Shopify.currency.rate 
          : 1;
          
        const thresholds = [
          {
            amount: (parseInt(tierComponent.getAttribute('data-threshold-1')) || 0) * currencyRate,
            discount: parseInt(tierComponent.getAttribute('data-discount-1')) || 10
          },
          {
            amount: (parseInt(tierComponent.getAttribute('data-threshold-2')) || 99900) * currencyRate,
            discount: parseInt(tierComponent.getAttribute('data-discount-2')) || 15
          },
          {
            amount: (parseInt(tierComponent.getAttribute('data-threshold-3')) || 199900) * currencyRate,
            discount: parseInt(tierComponent.getAttribute('data-discount-3')) || 20
          }
        ];
        
        if (ENABLE_LOGS) {
          console.log('[COLLECTION-TIER] Read thresholds from DOM:', thresholds);
        }
        
        return thresholds;
      } catch (error) {
        console.error('[COLLECTION-TIER] Error reading thresholds from DOM:', error);
        return [];
      }
    },
    
    updateBadgesWithAmount(cartAmount) {
      // Get tier discount settings from DOM (same way as tier-discounts-price component)
      const thresholds = this.getThresholdsFromDOM();
      
      if (!thresholds || thresholds.length === 0) {
        if (ENABLE_LOGS) {
          console.log('[COLLECTION-TIER] No thresholds found, hiding all badges');
        }
        // Hide all badges if no thresholds available
        const tierBadges = document.querySelectorAll('[data-tier-discount-badge]');
        tierBadges.forEach(badge => badge.style.display = 'none');
        return;
      }
      
      // Find current tier
      const sortedThresholds = [...thresholds].sort((a, b) => b.amount - a.amount);
      let currentDiscount = 0;
      
      for (const threshold of sortedThresholds) {
        if (cartAmount >= threshold.amount) {
          currentDiscount = threshold.discount;
          break;
        }
      }
      
      if (ENABLE_LOGS) {
        console.log('[COLLECTION-TIER] Updating badges, cart amount:', cartAmount, 'discount:', currentDiscount + '%');
      }
      
      // Update all tier discount badges on collection page
      const tierBadges = document.querySelectorAll('[data-tier-discount-badge]');
      tierBadges.forEach(badge => {
        const percentageElement = badge.querySelector('.tier-discount-percentage');
        
        if (currentDiscount > 0) {
          if (percentageElement) {
            percentageElement.textContent = currentDiscount;
          }
          badge.style.display = 'inline-block';
        } else {
          badge.style.display = 'none';
        }
      });
    }
  };
  
  // Auto-initialize on DOMContentLoaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      window.CollectionTierDiscountManager.init();
    });
  } else {
    // Document already loaded
    window.CollectionTierDiscountManager.init();
  }
}

// ---------------
// Dynamic Delivery Estimation
// ---------------
(function() {
  'use strict';

  /**
   * Adds business days to a date (skips weekends)
   * @param {Date} date - Starting date
   * @param {number} days - Number of business days to add
   * @returns {Date} - New date with business days added
   */
  function addBusinessDays(date, days) {
    let result = new Date(date);
    let addedDays = 0;
    
    while (addedDays < days) {
      result.setDate(result.getDate() + 1);
      // Skip weekends (0 = Sunday, 6 = Saturday)
      if (result.getDay() !== 0 && result.getDay() !== 6) {
        addedDays++;
      }
    }
    
    return result;
  }

  /**
   * Formats date as day number only for range formatting
   * @param {Date} date - Date to format
   * @returns {number} - Day number
   */
  function formatDay(date) {
    return date.getDate();
  }

  /**
   * Gets the current user locale from HTML lang attribute
   * @returns {string} - Locale string like "da" or "en"
   */
  function getUserLocale() {
    return document.documentElement.lang || 'en';
  }

  /**
   * Formats month name using user's locale
   * @param {Date} date - Date to format
   * @returns {string} - Month name like "August" or "august"
   */
  function formatMonth(date) {
    const locale = getUserLocale();
    const options = { month: 'long' };
    return date.toLocaleDateString(locale, options);
  }

  /**
   * Calculates and displays dynamic delivery dates
   * @param {HTMLElement} container - The estimate-shipping container
   */
  function calculateDeliveryDates(container) {
    console.log('[DEL] calculateDeliveryDates called', container);
    const minDays = parseInt(container.dataset.deliveryMin) || 2;
    const maxDays = parseInt(container.dataset.deliveryMax) || 4;
    console.log('[DEL] minDays:', minDays, 'maxDays:', maxDays);
    
    // Use current date/time for real-time calculation
    const buyDate = new Date();
    const minDeliveryDate = addBusinessDays(buyDate, minDays);
    const maxDeliveryDate = addBusinessDays(buyDate, maxDays);
    
    const deliveryDatesSpan = container.querySelector('.delivery-dates');
    const dynamicEstimate = container.querySelector('.dynamic-estimate');
    console.log('[DEL] deliveryDatesSpan:', deliveryDatesSpan, 'dynamicEstimate:', dynamicEstimate);
    
    if (deliveryDatesSpan && dynamicEstimate) {
      // Format the date range as "11-13 August"
      const minDay = formatDay(minDeliveryDate);
      const maxDay = formatDay(maxDeliveryDate);
      const monthName = formatMonth(minDeliveryDate);
      
      if (minDeliveryDate.toDateString() === maxDeliveryDate.toDateString()) {
        deliveryDatesSpan.textContent = `${minDay} ${monthName}`;
      } else if (minDeliveryDate.getMonth() === maxDeliveryDate.getMonth()) {
        // Same month: "11-13 August"
        deliveryDatesSpan.textContent = `${minDay}-${maxDay} ${monthName}`;
      } else {
        // Different months: "30 August - 2 September"
        const maxMonthName = formatMonth(maxDeliveryDate);
        deliveryDatesSpan.textContent = `${minDay} ${monthName} - ${maxDay} ${maxMonthName}`;
      }
      console.log('[DEL] Set delivery dates:', deliveryDatesSpan.textContent);
      
      // Update the full dynamic estimate text with configurable prefix and suffix
      updateDynamicEstimateText(container);
    } else {
      console.log('[DEL] Missing elements - deliveryDatesSpan or dynamicEstimate not found');
    }
  }

  /**
   * Updates the full dynamic estimate text with configurable prefix and suffix
   * @param {HTMLElement} container - The estimate-shipping container
   */
  function updateDynamicEstimateText(container) {
    const dynamicEstimate = container.querySelector('.dynamic-estimate');
    const deliveryDatesSpan = container.querySelector('.delivery-dates');
    const estimatePrefix = container.dataset.estimatePrefix || 'Forventet levering';
    const shippingText = container.dataset.shippingText || 'Fragt fra';
    const shippingPrice = container.dataset.shippingPrice || '39 kr';
    
    if (dynamicEstimate && deliveryDatesSpan) {
      // Update the text content while preserving the delivery dates span
      const datesText = deliveryDatesSpan.textContent;
      dynamicEstimate.innerHTML = `${estimatePrefix} <span class="delivery-dates">${datesText}</span>. ${shippingText} ${shippingPrice}`;
    }
  }

  /**
   * Initializes dynamic delivery estimation
   */
  function initDeliveryEstimation() {
    const estimateContainers = document.querySelectorAll('.estimate-shipping[data-delivery-min]');
    
    estimateContainers.forEach(container => {
      calculateDeliveryDates(container);
      
      // Show dynamic estimate instead of static metafield values
      const normalEstimate = container.querySelector('.normal-estimate');
      const extendedEstimate = container.querySelector('.extended');
      const dynamicEstimate = container.querySelector('.dynamic-estimate');
      
      if (normalEstimate && dynamicEstimate) {
        normalEstimate.style.display = 'none';
        if (extendedEstimate) extendedEstimate.style.display = 'none';
        dynamicEstimate.style.display = 'block';
      }
    });
  }

  /**
   * Updates delivery estimates when frame selection changes
   * @param {boolean} useExtended - Whether to use extended delivery times
   */
  function updateDeliveryEstimate(useExtended = false) {
    const estimateContainers = document.querySelectorAll('.estimate-shipping[data-delivery-min]');
    
    estimateContainers.forEach(container => {
      const normalEstimate = container.querySelector('.normal-estimate');
      const extendedEstimate = container.querySelector('.extended');
      const dynamicEstimate = container.querySelector('.dynamic-estimate');
      
      if (useExtended && extendedEstimate) {
        // Show static extended delivery from metafield
        normalEstimate.style.display = 'none';
        dynamicEstimate.style.display = 'none';
        extendedEstimate.style.display = 'block';
      } else {
        // Show dynamic calculation
        normalEstimate.style.display = 'none';
        if (extendedEstimate) extendedEstimate.style.display = 'none';
        dynamicEstimate.style.display = 'block';
        
        // Recalculate with current date/time
        calculateDeliveryDates(container);
        // Ensure the full text is updated with configurable values
        updateDynamicEstimateText(container);
      }
    });
  }

  // Export function to global scope for use by frame selection logic
  window.updateDeliveryEstimate = updateDeliveryEstimate;

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initDeliveryEstimation);
  } else {
    initDeliveryEstimation();
  }
})();


// Function of options appearance in the product card START

(function () {
  if (!window.matchMedia('(hover:hover) and (min-width: 992px)').matches) return;

  const root = document;

  const cartAdd = async (variantId, qty = 1) => {
    const body = JSON.stringify({ items: [{ id: Number(variantId), quantity: qty }] });
    const res = await fetch((window.Shopify?.routes?.root || '/') + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body
    });
    if (!res.ok) throw new Error('ATC failed');
    const data = await res.json();
    root.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: data }));
    return data;
  };

  const closeSelect = (wrap) => {
    if (!wrap) return;
    wrap.classList.remove('is-open');
    wrap.querySelector('[data-hqsel-toggle]')?.setAttribute('aria-expanded', 'false');
  };
  const openSelect = (wrap) => {
    if (!wrap) return;
    document.querySelectorAll('[data-hqsel].is-open').forEach(closeSelect);
    wrap.classList.add('is-open');
    wrap.querySelector('[data-hqsel-toggle]')?.setAttribute('aria-expanded', 'true');
  };
  const closeAllSelects = () => {
    document.querySelectorAll('[data-hqsel].is-open').forEach(closeSelect);
  };

  root.addEventListener('click', (e) => {
    const toggle = e.target.closest('[data-hqsel-toggle]');
    if (toggle) {
      const wrap = toggle.closest('[data-hqsel]');
      wrap.classList.contains('is-open') ? closeSelect(wrap) : openSelect(wrap);
      e.preventDefault();
      return;
    }

    const opt = e.target.closest('[data-hqsel-option]');
    if (opt) {
      if (opt.classList.contains('is-disabled')) return;
      const wrap    = opt.closest('[data-hqsel]');
      const labelEl = wrap.querySelector('[data-hqsel-current]');
      const native  = wrap.querySelector('.hqbar__native');
      const label   = opt.dataset.label || opt.textContent.trim();
      const id      = opt.dataset.variantId;

      wrap.querySelectorAll('[data-hqsel-option][aria-selected="true"]')
          .forEach(el => el.removeAttribute('aria-selected'));
      opt.setAttribute('aria-selected', 'true');

      if (labelEl) labelEl.textContent = label;
      if (native)  native.value = id;

      closeSelect(wrap);
      return;
    }

    const atcBtn = e.target.closest('[data-hqbar-atc]');
    if (atcBtn) {
      const card     = atcBtn.closest('.card.card--product.card--hq');
      const native   = card?.querySelector('.hqbar__native');
      const variantId = native?.value;

      if (!variantId) {
        const wrap = card?.querySelector('[data-hqsel]');
        openSelect(wrap);
        wrap?.querySelector('[data-hqsel-toggle]')?.focus();
        return;
      }

      atcBtn.disabled = true;
      cartAdd(variantId, 1)
        .then(() => { document.dispatchEvent(new CustomEvent('cart:open')); })
        .catch(console.error)
        .finally(() => { atcBtn.disabled = false; });
      return;
    }

    if (!e.target.closest('[data-hqsel]')) closeAllSelects();
  });

  root.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeAllSelects();
  });

  root.addEventListener('pointerout', (e) => {
    const card = e.target.closest('.card.card--product.card--hq');
    if (!card) return;
    if (e.relatedTarget && card.contains(e.relatedTarget)) return;
    closeAllSelects();
  }, true);
})();

// Function of options appearance in the product card END

// Priority Shipping Banner
(function() {
  'use strict';

  function log(...args) {
    if (ENABLE_LOGS) console.log('[Priority Shipping]', ...args);
  }

  async function addPriorityShippingToCart(variantId, button) {
    if (!variantId) {
      log('Error: No variant ID provided');
      return;
    }

    button.disabled = true;
    const originalText = button.textContent;
    button.textContent = '...';

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ id: variantId, quantity: 1 }),
      });

      const json = await response.json();

      if (!response.ok) {
        throw new Error(json.description || t('sections.cart.priority_shipping.error_adding', 'Error adding priority shipping to cart'));
      }

      log('Priority shipping added to cart', json);

      // Dispatch event to update cart drawer
      // The cart will re-render and the banner will be hidden by the Liquid template
      document.dispatchEvent(new CustomEvent('ajaxProduct:added'));

    } catch (error) {
      console.error('Error adding priority shipping:', error);
      button.textContent = originalText;
      button.disabled = false;
      alert(error.message || t('sections.cart.priority_shipping.error_adding', 'Failed to add priority shipping. Please try again.'));
    }
  }

  // Handle button clicks
  document.addEventListener('click', function(e) {
    const addButton = e.target.closest('[data-priority-shipping-add]');
    if (!addButton) return;

    e.preventDefault();
    const variantId = addButton.getAttribute('data-variant-id');
    log('Priority shipping add button clicked', variantId);
    addPriorityShippingToCart(variantId, addButton);
  });

})();
const mainContent1 = document.querySelector('main#MainContent');
const spanElement1 = document.querySelector('span.find-size');
const activeClass1 = 'side-drawer-open'; // Replace with your class
 
// Add class on span click
if (spanElement1 && mainContent1) {
  spanElement1.addEventListener('click', () => {
    mainContent1.classList.add(activeClass1);
  });
}
 
// Remove class on overlay click using event delegation
document.addEventListener('click', function(e) {
  // Check if the clicked element matches a.page-overlay or is inside it
  if (e.target.closest('a.page-overlay') || e.target.closest('.size-drawer--open')) {
    mainContent1.classList.remove(activeClass1);
  }
});
 // ---------------------------------------------------------------
// Poster Swiper — attribution
//
// Runs on every page, not just the swiper. It remembers which posters a
// visitor liked, then reports add-to-cart back to GA4 against that run and
// leaves the record in localStorage where the checkout web pixel reads it to
// attribute the purchase. Purely observational: it never alters cart requests.
// ---------------------------------------------------------------
(function() {
  var STORAGE_KEY = 'ps_attr_v1';
  var ATTR_WINDOW_MS = 7 * 24 * 60 * 60 * 1000; // a liked poster stays attributable for 7 days
  var MAX_PARAM_LEN = 100; // GA4 truncates event parameter values beyond this

  function noop() {}

  function truncate(str) {
    str = String(str == null ? '' : str);
    return str.length > MAX_PARAM_LEN ? str.slice(0, MAX_PARAM_LEN) : str;
  }

  /** Store identity, so one GA4 property can report peleton-aps and peleton-ww apart. */
  function shopContext() {
    var shop = (window.Shopify && window.Shopify.shop) || window.location.hostname;
    return {
      shop:       shop,
      shop_label: shop.indexOf('peleton-ww') === 0 ? 'worldwide' : 'denmark',
      currency:   (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || null
    };
  }

  function read() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      var attr = JSON.parse(raw);
      if (!attr || !attr.ts || Date.now() - attr.ts > ATTR_WINDOW_MS) {
        clear();
        return null;
      }
      return attr;
    } catch (e) {
      return null;
    }
  }

  function write(attr) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(attr));
    } catch (e) {}
  }

  function clear() {
    try { window.localStorage.removeItem(STORAGE_KEY); } catch (e) {}
  }

  /**
   * Records a completed swiper run.
   * @param {{sessionId:string, handles:string[], vendors:string[], source?:string}} run
   */
  function save(run) {
    if (!run || !run.handles) return;
    write({
      v:       1,
      sid:     run.sessionId || null,
      ts:      Date.now(),
      source:  run.source || 'swiper',
      shop:    shopContext().shop,
      handles: run.handles.slice(0, 20),
      vendors: (run.vendors || []).slice(0, 20)
    });
  }

  function track(eventName, params) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event: eventName }, shopContext(), params));
  }

  /** Pulls a product handle off an AJAX cart line item, whichever fields it carries. */
  function lineItemHandle(item) {
    if (!item) return '';
    if (item.handle) return item.handle;
    var url = item.url || '';
    var match = url.match(/\/products\/([^?#\/]+)/);
    return match ? match[1] : '';
  }

  /**
   * Reports an add-to-cart that followed a swiper run.
   * ps_match distinguishes a poster the visitor actually liked from anything
   * else they bought in the attribution window, so the two can be split in GA4.
   */
  function reportAdd(item) {
    var attr = read();
    if (!attr) return;

    var handle = lineItemHandle(item);
    var liked = handle && attr.handles.indexOf(handle) !== -1;
    var cents = parseInt(item.price, 10);
    var quantity = parseInt(item.quantity, 10) || 1;

    track('poster_swiper_add_to_cart', {
      ps_session_id:     attr.sid,
      ps_match:          liked ? 'liked' : 'session',
      ps_source:         attr.source,
      product_handle:    truncate(handle),
      product_title:     truncate(item.product_title || item.title),
      product_vendor:    truncate(item.vendor),
      product_variant:   truncate(item.variant_title),
      quantity:          quantity,
      value:             isNaN(cents) ? null : (cents * quantity) / 100,
      hours_since_swipe: Math.round((Date.now() - attr.ts) / 36000) / 100
    });
  }

  /** Handles both /cart/add.js response shapes: a bare line item, or {items:[...]}. */
  function onCartAdd(data) {
    if (!data) return;
    var items = data.items && data.items.length ? data.items : [data];
    items.forEach(function(item) {
      try { reportAdd(item); } catch (e) {}
    });
  }

  // Every add-to-cart path in this theme goes through fetch('/cart/add.js'), so one
  // passive wrapper covers product pages, quick-buy, frame popup and the builder.
  function interceptCartAdds() {
    if (!window.fetch || window.__psCartHooked) return;
    window.__psCartHooked = true;

    var originalFetch = window.fetch;
    window.fetch = function(input, init) {
      var promise = originalFetch.apply(this, arguments);
      try {
        var url = typeof input === 'string' ? input : (input && input.url) || '';
        if (/\/cart\/add(\.js)?(\?|$)/.test(url)) {
          promise.then(function(response) {
            if (!response || !response.ok) return;
            try {
              response.clone().json().then(onCartAdd).catch(noop);
            } catch (e) {}
          }).catch(noop);
        }
      } catch (e) {}
      return promise;
    };
  }

  interceptCartAdds();

  window.PosterSwiperAttribution = {
    shopContext: shopContext,
    save: save,
    read: read,
    clear: clear
  };
})();

 // ---------------
// Poster Swiper
// ---------------
(function() {
  var BATCH_SIZE = 30;
  var LIKES_GOAL = 5;
  var SWIPE_THRESHOLD = 80;
  var _cfg = window.posterSwiperConfig || {};
  var COLLECTION = _cfg.collectionHandle || 'posters';
  var BESTSELLERS = _cfg.bestsellersHandle || 'bestsellers';
  var MONEY_FORMAT = _cfg.moneyFormat || '{{amount}} kr';
  var STR = Object.assign({ likeOverlay: 'GEM', skipOverlay: 'SKIP', likeLabel: 'Gemt ♥', pricePrefix: 'Fra', vendorBy: 'by' }, _cfg.strings || {});
  var CONFETTI_COLORS = ['#c9a96e', '#111', '#d4c5b2', '#8b7355', '#e8e0d5'];

  var state = {
    products: [],
    currentIndex: 0,
    likes: [],
    history: [],
    page: 1,
    hasNextPage: true,
    isFetching: false,
    swipeInProgress: false
  };

  var analytics = {
    sessionStart: null,
    totalSwipes: 0,
    considerationTotalMs: 0,
    sessionId: null
  };

  // Time a single card spends on top of the stack, paused while the tab is hidden
  // so a backgrounded tab never inflates the consideration numbers.
  var CONSIDERATION_CAP_MS = 300000; // 5 min — clamps "walked away" outliers
  // `active` marks that a card is currently up for consideration; `running` marks
  // that the clock is ticking. They differ while the tab sits in the background.
  var consideration = { startedAt: 0, accumulatedMs: 0, running: false, active: false };

  function considerationStart() {
    consideration.accumulatedMs = 0;
    consideration.startedAt = 0;
    consideration.running = false;
    consideration.active = true;
    considerationResume();
  }

  function considerationPause() {
    if (!consideration.running) return;
    consideration.accumulatedMs += Date.now() - consideration.startedAt;
    consideration.running = false;
  }

  function considerationResume() {
    if (!consideration.active || consideration.running) return;
    if (document.visibilityState === 'hidden') return;
    consideration.startedAt = Date.now();
    consideration.running = true;
  }

  /** Stops the timer and returns elapsed visible milliseconds (clamped). */
  function considerationStop() {
    considerationPause();
    consideration.active = false;
    return Math.min(consideration.accumulatedMs, CONSIDERATION_CAP_MS);
  }

  document.addEventListener('visibilitychange', function() {
    if (document.visibilityState === 'hidden') considerationPause();
    else considerationResume();
  });

  /** Stable per-swiper-run id so add-to-cart and purchase can be joined back to a run. */
  function newSessionId() {
    return 'ps-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8);
  }

  /** Store dimension — lets one GA4 property report peleton-aps and peleton-ww apart. */
  function shopContext() {
    var attr = window.PosterSwiperAttribution;
    return attr ? attr.shopContext() : {};
  }

  function track(eventName, params) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign(
      { event: eventName },
      shopContext(),
      analytics.sessionId ? { ps_session_id: analytics.sessionId } : {},
      params
    ));
  }

  var els = {};

  function init() {
    els.root = document.getElementById('poster-swiper');
    if (!els.root) return;

    els.intro = document.getElementById('ps-intro');
    els.swiper = document.getElementById('ps-swiper');
    els.results = document.getElementById('ps-results');
    els.stack = document.getElementById('ps-stack');
    els.loading = document.getElementById('ps-loading');
    els.hearts = document.getElementById('ps-hearts');
    els.reveal = document.getElementById('ps-reveal');
    els.revealCircle = document.getElementById('ps-reveal-circle');
    els.revealPretext = document.getElementById('ps-reveal-pretext');
    els.resultsGrid = document.getElementById('ps-results-grid');
    els.celebration = document.getElementById('ps-celebration');
    els.likeBtn = document.getElementById('ps-like-btn');
    els.skipBtn = document.getElementById('ps-skip-btn');
    els.undoBtn = document.getElementById('ps-undo-btn');
    els.demoCard = document.getElementById('ps-demo-card');
    els.startBtn = document.getElementById('ps-start-btn');
    els.restartBtn = document.getElementById('ps-restart-btn');
    els.copyBtn = document.getElementById('ps-copy-btn');

    els.likeBtn.addEventListener('click', function() { onButtonSwipe('like'); });
    els.skipBtn.addEventListener('click', function() { onButtonSwipe('skip'); });
    els.undoBtn.addEventListener('click', undoSwipe);
    els.startBtn.addEventListener('click', startSwiper);
    els.restartBtn.addEventListener('click', restart);
    if (els.copyBtn) els.copyBtn.addEventListener('click', copyLink);

    // Check URL for saved likes — skip intro if present
    var urlParams = new URLSearchParams(window.location.search);
    var savedLikes = urlParams.get('likes');
    if (savedLikes) {
      var handles = savedLikes.split(',').filter(Boolean).slice(0, LIKES_GOAL);
      if (handles.length > 0) {
        if (els.intro) els.intro.style.display = 'none';
        loadResultsFromHandles(handles);
        return;
      }
    }

    // Track result card clicks
    if (els.resultsGrid) {
      els.resultsGrid.addEventListener('click', function(e) {
        var link = e.target.closest('a[href*="/products/"]');
        if (!link) return;
        var handle = (link.getAttribute('href') || '').replace(/.*\/products\//, '').split('?')[0];
        var card = link.closest('.card--product');
        var cards = Array.prototype.slice.call(els.resultsGrid.querySelectorAll('.card--product'));
        track('poster_swiper_result_click', {
          product_handle: handle,
          result_position: card ? cards.indexOf(card) + 1 : null
        });
      });
    }

    // Pre-fetch in background while user reads intro
    fetchBatch();
  }

  /**
   * Transitions from intro screen to swiper view
   */
  /**
   * Replaces the placeholder art in the demo card with real poster images
   * @param {Array} products
   */
  function populateDemoCard(products) {
    if (!els.demoCard) return;

    // Pick up to 5 products that have images
    var imgs = products
      .filter(function(p) { return p.images && p.images[0]; })
      .slice(0, 5)
      .map(function(p) {
        var tags = productTags(p);
        var fit = tags.indexOf('landskab') !== -1 ? 'contain' : 'cover';
        return { url: resizeImage(p.images[0].src, '400x533'), fit: fit };
      });

    if (imgs.length === 0) return;

    // Replace the art placeholder with a real image
    var art = els.demoCard.querySelector('.poster-swiper__demo-card-art');
    if (art) art.remove();

    // Preload all images so swaps are instant
    imgs.forEach(function(item) { var pi = new Image(); pi.src = item.url; });

    // Two img layers for crossfade
    var imgA = document.createElement('img');
    imgA.className = 'poster-swiper__demo-card-img';
    imgA.alt = '';
    imgA.style.opacity = '1';

    var imgB = document.createElement('img');
    imgB.className = 'poster-swiper__demo-card-img';
    imgB.alt = '';
    imgB.style.opacity = '0';

    els.demoCard.appendChild(imgA);
    els.demoCard.appendChild(imgB);

    imgA.src = imgs[0].url;
    imgA.style.objectFit = imgs[0].fit;

    if (imgs.length < 2) return;

    // ps-demo-rock is 3.6s:
    //   skip returns to center at 36% = 1296ms  ← swap here
    //   like returns to center at 76% = 2736ms  ← swap here
    var CYCLE  = 3600;
    var SWAP_1 = 1296;
    var SWAP_2 = 2736;

    var idx   = 0;
    var front = imgA, back = imgB;
    var t1, t2;

    function swap() {
      idx = (idx + 1) % imgs.length;
      back.src = imgs[idx].url;
      back.style.objectFit = imgs[idx].fit;
      front.style.opacity = '0';
      back.style.opacity  = '1';
      var tmp = front; front = back; back = tmp;
    }

    function scheduleSwaps(offset) {
      // offset = how far we are into the current cycle already (ms)
      clearTimeout(t1); clearTimeout(t2);
      var d1 = SWAP_1 - offset;
      var d2 = SWAP_2 - offset;
      if (d1 > 0) t1 = setTimeout(swap, d1);
      if (d2 > 0) t2 = setTimeout(swap, d2);
    }

    // On every animation cycle restart, schedule both swaps
    els.demoCard.addEventListener('animationiteration', function(e) {
      if (e.animationName !== 'ps-demo-rock') return;
      scheduleSwaps(0);
    });

    // Sync with current cycle position if animation already running
    var offset = 0;
    try {
      var anims = els.demoCard.getAnimations ? els.demoCard.getAnimations() : [];
      for (var i = 0; i < anims.length; i++) {
        if (anims[i].animationName === 'ps-demo-rock') {
          offset = anims[i].currentTime % CYCLE;
          break;
        }
      }
    } catch (e) {}
    scheduleSwaps(offset);
  }

  function startSwiper() {
    analytics.sessionStart = Date.now();
    analytics.totalSwipes = 0;
    analytics.considerationTotalMs = 0;
    analytics.sessionId = newSessionId();
    track('poster_swiper_start');
    if (els.intro) {
      els.intro.style.opacity = '0';
      els.intro.style.transition = 'opacity 0.3s ease';
      setTimeout(function() {
        els.intro.style.display = 'none';
        els.swiper.classList.add('is-active');
        renderStack();
      }, 300);
    } else {
      els.swiper.classList.add('is-active');
      renderStack();
    }
  }

  /**
   * Fetches the next page of products via Shopify AJAX API (no token required)
   */
  function fetchPage(page, sortBy) {
    var url = '/collections/' + COLLECTION + '/products.json?limit=' + BATCH_SIZE + '&page=' + page;
    if (sortBy) url += '&sort_by=' + sortBy;
    return fetch(url).then(function(res) { return res.json(); }).then(function(data) { return (data && data.products) || []; });
  }

  // Interleaves bestsellers into rest: 1 bestseller every `interval` cards
  function interleaveProducts(bestsellers, rest, interval) {
    var result = [];
    var bi = 0, ri = 0, count = 0;
    while (bi < bestsellers.length || ri < rest.length) {
      if (count % interval === 0 && bi < bestsellers.length) {
        result.push(bestsellers[bi++]);
      } else if (ri < rest.length) {
        result.push(rest[ri++]);
      } else {
        result.push(bestsellers[bi++]);
      }
      count++;
    }
    return result;
  }

  // Ensures products from a given vendor appear no closer than minGap cards apart.
  // Starts inserting the vendor's products after a random offset so it never leads.
  function enforceMinGap(products, vendor, minGap) {
    var isVendor = function(p) { return (p.vendor || '').toLowerCase() === vendor.toLowerCase(); };
    var special = products.filter(isVendor);
    var others  = products.filter(function(p) { return !isVendor(p); });
    if (special.length === 0) return products;

    var result = [];
    var si = 0, oi = 0;
    var sinceLastSpecial = minGap + 1;
    var startOffset = Math.floor(Math.random() * 4) + 3; // first special no earlier than pos 3–6

    while (oi < others.length || si < special.length) {
      if (si < special.length && sinceLastSpecial > minGap && result.length >= startOffset) {
        result.push(special[si++]);
        sinceLastSpecial = 0;
      } else if (oi < others.length) {
        result.push(others[oi++]);
        sinceLastSpecial++;
      } else {
        result.push(special[si++]);
        sinceLastSpecial = 0;
      }
    }
    return result;
  }

  function fetchBatch() {
    if (state.isFetching || !state.hasNextPage) return;

    state.isFetching = true;
    showLoading(true);

    var isFirstBatch = state.products.length === 0;

    var fetcher;
    if (isFirstBatch) {
      // Fetch top bestsellers + a random page for variety.
      // Interleave 1 bestseller every 3 cards so they appear early and naturally.
      var randomPage = Math.floor(Math.random() * 4) + 2; // random page 2–5
      fetcher = Promise.all([
        fetch('/collections/' + BESTSELLERS + '/products.json?limit=30').then(function(r) { return r.json(); }).then(function(d) { return (d && d.products) || []; }),
        fetchPage(randomPage)
      ]).then(function(results) {
        var bestsellers = results[0].slice(0, 20);
        shuffleArray(bestsellers);
        // Pull out first card guarantee before shuffling the rest into the deck
        var firstCard = bestsellers.shift();
        var rest = results[1];
        shuffleArray(rest);
        var combined = interleaveProducts(bestsellers, rest, 3);
        combined = enforceMinGap(combined, 'Martin Jørgensen', 6);
        combined.unshift(firstCard);
        state.page = randomPage + 1;
        state.hasNextPage = results[1].length === BATCH_SIZE;
        return combined;
      });
    } else {
      fetcher = fetchPage(state.page).then(function(products) {
        state.page++;
        state.hasNextPage = products.length === BATCH_SIZE;
        return products;
      });
    }

    fetcher
      .then(function(newProducts) {
        state.products = state.products.concat(newProducts);
        state.isFetching = false;
        showLoading(false);
        if (isFirstBatch) {
          // Fetch demo images from a late page so they don't spoil the swipe deck
          var demoPage = Math.floor(Math.random() * 3) + 8; // page 8–10
          fetch('/collections/' + COLLECTION + '/products.json?limit=10&page=' + demoPage)
            .then(function(r) { return r.json(); })
            .then(function(d) { populateDemoCard((d && d.products) || []); });
          newProducts.slice(0, 8).forEach(function(product) {
            var images = product.images || [];
            var image = images[1] || images[0];
            if (image) { var pi = new Image(); pi.src = resizeImage(image.src, '800x1067'); }
          });
        }
        renderStack();
      })
      .catch(function(err) {
        if (ENABLE_LOGS) console.log('Poster swiper fetch error:', err);
        state.isFetching = false;
        showLoading(false);
      });
  }

  /**
   * Renders up to 3 cards in the stack, top card gets drag events
   */
  function preloadUpcoming() {
    // Preload images for the next 4 cards beyond what's currently visible
    var upcoming = state.products.slice(state.currentIndex + 2, state.currentIndex + 6);
    upcoming.forEach(function(product) {
      var images = product.images || [];
      var image = images[1] || images[0];
      if (image) {
        var pi = new Image();
        pi.src = resizeImage(image.src, '800x1067');
      }
    });
  }

  function renderStack() {
    if (!els.stack) return;
    els.stack.innerHTML = '';

    var visible = state.products.slice(state.currentIndex, state.currentIndex + 2);
    if (visible.length === 0) {
      handleEmpty();
      return;
    }

    // Insert in reverse order so top card is last child (highest z-index)
    for (var i = visible.length - 1; i >= 0; i--) {
      var card = buildCard(visible[i], i);
      els.stack.appendChild(card);
    }

    attachDragEvents(els.stack.lastElementChild);
    considerationStart();
    preloadUpcoming();
  }

  /**
   * Builds a single swiper card element
   * @param {Object} product - Shopify AJAX API product object
   * @param {number} stackIndex - Position in stack (0 = top)
   */
  function buildCard(product, stackIndex) {
    var card = document.createElement('div');
    card.className = 'poster-swiper__card poster-swiper__card--' + stackIndex;
    card.dataset.handle = product.handle;

    var images = product.images || [];
    // Show images 2-5 (indices 1-4); fall back to first image if needed
    var galleryImages = images.slice(1, 4);
    if (galleryImages.length === 0) galleryImages = images.slice(0, 1);

    var galleryUrls = galleryImages.map(function(img) { return resizeImage(img.src, '800x1067'); });
    card.dataset.galleryUrls = JSON.stringify(galleryUrls);
    card.dataset.galleryIndex = '0';
    card.dataset.galleryViews = '0';

    var imageAlt = (galleryImages[0] && galleryImages[0].alt) || product.title;

    // Preload remaining gallery images
    galleryUrls.slice(1).forEach(function(url) { var pi = new Image(); pi.src = url; });

    var tags = productTags(product);
    var isLandscape = tags.indexOf('landskab') !== -1;
    var imgStyle = isLandscape ? ' style="object-fit:contain"' : '';

    var imgHtml = galleryUrls[0]
      ? '<img class="poster-swiper__card-image" src="' + galleryUrls[0] + '" alt="' + escapeAttr(imageAlt) + '" draggable="false"' + imgStyle + '>'
      : '';

    var tabsHtml = galleryUrls.length > 1
      ? '<div class="ps-gallery-tabs">' + galleryUrls.map(function(_, i) {
          return '<div class="ps-gallery-tab' + (i === 0 ? ' ps-gallery-tab--active' : '') + '"></div>';
        }).join('') + '</div>'
      : '';

    card.innerHTML = [
      tabsHtml,
      '<div class="poster-swiper__card-like-overlay"><span>' + STR.likeOverlay + '</span></div>',
      '<div class="poster-swiper__card-skip-overlay"><span>' + STR.skipOverlay + '</span></div>',
      imgHtml,
      '<div class="poster-swiper__card-info">',
      '  <span class="poster-swiper__card-vendor">' + escapeHtml(product.vendor) + '</span>',
      '  <span class="poster-swiper__card-title">' + escapeHtml(product.title) + '</span>',
      '</div>'
    ].join('');

    return card;
  }

  function navigateGallery(card, dir) {
    var urls = JSON.parse(card.dataset.galleryUrls || '[]');
    if (urls.length <= 1) return;
    var index = parseInt(card.dataset.galleryIndex || '0', 10);
    var newIndex = Math.max(0, Math.min(urls.length - 1, index + dir));
    if (newIndex === index) return;
    card.dataset.galleryIndex = String(newIndex);
    card.dataset.galleryViews = String(parseInt(card.dataset.galleryViews || '0', 10) + 1);
    var img = card.querySelector('.poster-swiper__card-image');
    if (img) img.src = urls[newIndex];
    card.querySelectorAll('.ps-gallery-tab').forEach(function(tab, i) {
      tab.classList.toggle('ps-gallery-tab--active', i === newIndex);
    });
  }

  /**
   * Attaches pointer-based drag events to the top card
   * @param {HTMLElement} card
   */
  function attachDragEvents(card) {
    var startX = 0;
    var startClientX = 0;
    var isDragging = false;
    var currentX = 0;
    var likeOverlay = card.querySelector('.poster-swiper__card-like-overlay');
    var skipOverlay = card.querySelector('.poster-swiper__card-skip-overlay');

    card.addEventListener('pointerdown', function(e) {
      startX = e.clientX;
      startClientX = e.clientX;
      isDragging = true;
      currentX = 0;
      card.setPointerCapture(e.pointerId);
    });

    card.addEventListener('pointermove', function(e) {
      if (!isDragging) return;
      currentX = e.clientX - startX;
      var rotate = currentX * 0.10;
      card.style.transition = 'none';
      card.style.transform = 'translateX(' + currentX + 'px) rotate(' + rotate + 'deg)';

      // Start showing overlay from 20px drag, reach full opacity at threshold
      var progress = Math.max(0, (Math.abs(currentX) - 20) / (SWIPE_THRESHOLD - 20));
      if (currentX > 0) {
        likeOverlay.style.opacity = Math.min(progress, 1);
        skipOverlay.style.opacity = 0;
      } else {
        skipOverlay.style.opacity = Math.min(progress, 1);
        likeOverlay.style.opacity = 0;
      }
    });

    card.addEventListener('pointerup', function(e) {
      if (!isDragging) return;
      isDragging = false;

      if (currentX > SWIPE_THRESHOLD) {
        flyOut(card, 'like');
      } else if (currentX < -SWIPE_THRESHOLD) {
        flyOut(card, 'skip');
      } else {
        // Snap back
        card.style.transition = 'transform 0.3s ease';
        card.style.transform = '';
        likeOverlay.style.opacity = 0;
        skipOverlay.style.opacity = 0;
        state.swipeInProgress = false;

        // Treat as gallery tap if movement was minimal
        if (Math.abs(currentX) < 8) {
          var rect = card.getBoundingClientRect();
          var tapX = startClientX - rect.left;
          navigateGallery(card, tapX > rect.width / 2 ? 1 : -1);
        }
      }
    });

    card.addEventListener('pointercancel', function() {
      isDragging = false;
      state.swipeInProgress = false;
      card.style.transition = 'transform 0.3s ease';
      card.style.transform = '';
      likeOverlay.style.opacity = 0;
      skipOverlay.style.opacity = 0;
    });
  }

  /**
   * Animates card flying off screen, then commits the swipe
   * @param {HTMLElement} card
   * @param {string} action - 'like' or 'skip'
   */
  function flyOut(card, action) {
    if (state.swipeInProgress) return;
    state.swipeInProgress = true;
    var dir = action === 'like' ? 1 : -1;
    card.style.transition = 'transform 0.35s ease, opacity 0.3s ease';
    card.style.transform = 'translateX(' + (dir * 130) + '%) rotate(' + (dir * 18) + 'deg)';
    card.style.opacity = '0';
    setTimeout(function() { commitSwipe(action); }, 220);
  }

  /**
   * Handles button click swipe (like or skip) with bounce feedback
   * @param {string} action
   */
  function onButtonSwipe(action) {
    var topCard = els.stack && els.stack.lastElementChild;
    if (!topCard || !topCard.classList.contains('poster-swiper__card')) return;

    var btn = action === 'like' ? els.likeBtn : els.skipBtn;
    if (btn) {
      btn.classList.remove('is-pressed');
      void btn.offsetWidth; // reflow to restart animation
      btn.classList.add('is-pressed');
      setTimeout(function() { btn.classList.remove('is-pressed'); }, 400);
    }

    flyOut(topCard, action);
  }

  /**
   * Records the swipe result and advances to next card
   * @param {string} action - 'like' or 'skip'
   */
  function commitSwipe(action) {
    var product = state.products[state.currentIndex];
    if (!product) return;

    var swipedCardEl = els.stack && els.stack.lastElementChild;
    var galleryViews = swipedCardEl ? parseInt(swipedCardEl.dataset.galleryViews || '0', 10) : 0;
    var considerationMs = considerationStop();
    analytics.considerationTotalMs += considerationMs;

    analytics.totalSwipes++;
    track('poster_swiper_swipe', {
      swipe_action:           action,
      product_handle:         product.handle,
      product_title:          product.title,
      product_vendor:         product.vendor,
      product_price:          priceAmount(product),
      currency:               currencyCode(),
      swipe_number:           analytics.totalSwipes,
      likes_so_far:           state.likes.length + (action === 'like' ? 1 : 0),
      consideration_ms:       considerationMs,
      consideration_seconds:  Math.round(considerationMs / 100) / 10,
      gallery_views:          galleryViews
    });

    if (action === 'like') {
      state.likes.push(product);
      updateHearts();
      triggerLikeEffect();
    }

    state.history.push({ action: action, product: product });
    state.currentIndex++;
    updateUndoBtn();

    // Pre-fetch next batch if running low
    var remaining = state.products.length - state.currentIndex;
    if (remaining < 8 && state.hasNextPage && !state.isFetching) {
      fetchBatch();
    }

    if (state.likes.length >= LIKES_GOAL) {
      state.swipeInProgress = false;
      setTimeout(showResults, 300);
      return;
    }

    // Targeted stack update — avoids clearing DOM so the back card doesn't flash
    if (swipedCardEl) els.stack.removeChild(swipedCardEl);

    var promotedCard = els.stack && els.stack.lastElementChild;
    if (promotedCard) {
      // Cancel any ongoing fade-in and lock opacity before changing class
      promotedCard.style.animation = 'none';
      promotedCard.style.opacity = '1';
      void promotedCard.offsetWidth; // flush so the above applies before class change
      promotedCard.className = 'poster-swiper__card poster-swiper__card--0 poster-swiper__card--promoted';
      attachDragEvents(promotedCard);
      considerationStart(); // next card is now on top — restart the per-product timer
    } else {
      handleEmpty();
      state.swipeInProgress = false;
      return;
    }

    // Insert new back card
    var upcomingProduct = state.products[state.currentIndex + 1];
    if (upcomingProduct) {
      var newBackCard = buildCard(upcomingProduct, 1);
      els.stack.insertBefore(newBackCard, els.stack.firstChild);
    }

    preloadUpcoming();
    state.swipeInProgress = false;
  }

  /**
   * Undoes the last swipe
   */
  function undoSwipe() {
    if (state.history.length === 0) return;

    var last = state.history.pop();
    state.currentIndex--;

    if (last.action === 'like') {
      state.likes.pop();
      // Unfill last heart
      if (els.hearts) {
        var heart = els.hearts.querySelector('[data-index="' + state.likes.length + '"]');
        if (heart) heart.classList.remove('is-filled');
      }
    }

    updateUndoBtn();
    renderStack();
  }

  function updateUndoBtn() {
    if (els.undoBtn) {
      els.undoBtn.disabled = state.history.length === 0;
    }
  }

  function handleEmpty() {
    if (state.likes.length > 0) {
      showResults();
    } else {
      els.stack.innerHTML = '<p class="poster-swiper__empty">Ingen flere plakater at vise.</p>';
    }
  }

  /**
   * Fills in the next heart icon with a pop animation
   */
  function updateHearts() {
    if (!els.hearts) return;
    var index = state.likes.length - 1;
    var heart = els.hearts.querySelector('[data-index="' + index + '"]');
    if (heart) {
      heart.classList.add('is-filled');
    }
  }

  /**
   * Shows the reveal animation, then transitions to results
   */
  function showResults() {
    var duration = analytics.sessionStart ? Math.round((Date.now() - analytics.sessionStart) / 1000) : null;
    var avgMs = analytics.totalSwipes ? analytics.considerationTotalMs / analytics.totalSwipes : 0;
    track('poster_swiper_complete', {
      total_swipes:               analytics.totalSwipes,
      duration_seconds:           duration,
      avg_consideration_seconds:  Math.round(avgMs / 100) / 10,
      liked_handles:              state.likes.map(function(p) { return p.handle; }).join(','),
      liked_vendors:              state.likes.map(function(p) { return p.vendor; }).join(',')
    });

    // Persist the run so add-to-cart and purchase can be attributed back to the swiper,
    // including on later visits and on product pages away from this one.
    if (window.PosterSwiperAttribution) {
      window.PosterSwiperAttribution.save({
        sessionId: analytics.sessionId,
        handles:   state.likes.map(function(p) { return p.handle; }),
        vendors:   state.likes.map(function(p) { return p.vendor; })
      });
    }

    buildResultsGrid(state.likes);
    updateUrl(state.likes.map(function(p) { return p.handle; }));
    els.swiper.classList.remove('is-active');
    showReveal(state.likes);
  }

  /**
   * Builds and plays the circle reveal screen
   * @param {Array} products
   */
  function showReveal(products) {
    if (!els.reveal || !els.revealCircle) {
      els.results.style.display = 'block';
      spawnConfetti();
      return;
    }

    var timers = [];
    var done = false;

    function scheduleTransition() {
      if (done) return;
      done = true;
      timers.forEach(clearTimeout);
      transitionToResults();
    }

    // Pre-build circle items (hidden until phase 1)
    els.revealCircle.innerHTML = '';
    var angles = [-90, -18, 54, 126, 198];
    // Alternate from-x: odd items from left, even from right
    var fromXValues = [520, -520, 520, -520, 520];
    var items = [];
    products.forEach(function(product, i) {
      var productImages = product.images || [];
      var image = productImages[productImages.length - 1] || productImages[0];
      var imageUrl = image ? resizeImage(image.src, '300x400') : '';
      var item = document.createElement('div');
      item.className = 'ps-reveal-item';
      item.style.setProperty('--angle', angles[i] + 'deg');
      item.style.setProperty('--from-x', fromXValues[i] + 'px');
      item.style.setProperty('--to-x', fromXValues[i] + 'px');
      // No delay yet — will be set when phase 1 starts
      item.style.setProperty('--item-delay', '9999s'); // hidden until triggered
      if (imageUrl) {
        item.innerHTML = '<img src="' + imageUrl + '" alt="' + escapeAttr(product.title) + '">';
      }
      els.revealCircle.appendChild(item);
      items.push(item);
    });

    els.reveal.classList.add('is-active');

    // --- Phase 0 (0–2400ms): "Her er dit personlige valg" fades in then out ---
    if (els.revealPretext) {
      els.revealPretext.classList.remove('is-visible', 'is-leaving');
      // Force reflow so animation replays
      void els.revealPretext.offsetWidth;
      els.revealPretext.classList.add('is-visible');

      timers.push(setTimeout(function() {
        els.revealPretext.classList.remove('is-visible');
        els.revealPretext.classList.add('is-leaving');
      }, 1800));

      timers.push(setTimeout(function() {
        els.revealPretext.classList.remove('is-leaving');
      }, 2500));
    }

    // --- Phase 1 (2200ms): items fly in from left/right ---
    // Stagger 200ms, duration 1000ms → last item lands at 2200 + 800 + 1000 = 4000ms
    var flyInStart = 2200;
    timers.push(setTimeout(function() {
      items.forEach(function(item, i) {
        item.style.setProperty('--item-delay', (i * 200) + 'ms');
        // Force animation restart
        item.style.animation = 'none';
        void item.offsetWidth;
        item.style.animation = '';
      });
    }, flyInStart));

    // Spinning starts mid fly-in
    timers.push(setTimeout(function() {
      els.revealCircle.classList.add('is-spinning');
    }, flyInStart + 600));

    // --- Phase 2 hold: all items in by ~4000ms, hold until 5000ms ---

    // --- Phase 3 (5000ms): fly out one by one ---
    // Stagger 160ms, duration 720ms → last out at 5000 + 640 + 720 = 6360ms
    var flyOutStart = 5000;
    items.forEach(function(item, i) {
      timers.push(setTimeout(function() {
        item.classList.add('is-flying-out');
      }, flyOutStart + i * 160));
    });

    // --- Phase 4 (6500ms): fade to results ---
    timers.push(setTimeout(scheduleTransition, 6500));

    // Tap anywhere to skip
    els.reveal.addEventListener('click', scheduleTransition, { once: true });

    function transitionToResults() {
      if (els.revealPretext) {
        els.revealPretext.classList.remove('is-visible', 'is-leaving');
      }
      els.reveal.classList.add('is-leaving');
      setTimeout(function() {
        els.reveal.classList.remove('is-active', 'is-leaving');
        els.revealCircle.classList.remove('is-spinning');
        els.results.style.display = 'block';
        spawnConfetti();
      }, 800);
    }
  }

  /**
   * Spawns confetti particles in the celebration container
   */
  function spawnConfetti() {
    if (!els.celebration) return;
    els.celebration.innerHTML = '';
    for (var i = 0; i < 18; i++) {
      (function(i) {
        var dot = document.createElement('div');
        dot.className = 'ps-confetti-dot';
        dot.style.left = (Math.random() * 100) + '%';
        dot.style.top = (Math.random() * 30) + '%';
        dot.style.background = CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)];
        dot.style.animationDelay = (Math.random() * 0.4) + 's';
        dot.style.animationDuration = (0.7 + Math.random() * 0.4) + 's';
        dot.style.width = (5 + Math.random() * 5) + 'px';
        dot.style.height = dot.style.width;
        els.celebration.appendChild(dot);
      })(i);
    }
  }

  /**
   * Spawns floating hearts and a glow on the top card when a like is registered
   */
  function triggerLikeEffect() {
    var card = els.stack && els.stack.lastElementChild;

    // Glow on card
    if (card) {
      card.classList.add('poster-swiper__card--like-glow');
    }

    // Floating hearts from the card's position
    var rect = els.stack ? els.stack.getBoundingClientRect() : null;
    if (!rect) return;

    var centerX = rect.left + rect.width / 2;
    var centerY = rect.top + rect.height * 0.5;

    // "Gemt!" label
    var label = document.createElement('div');
    label.className = 'ps-like-label';
    label.textContent = STR.likeLabel;
    label.style.left = (centerX - 30) + 'px';
    label.style.top = (centerY - 20) + 'px';
    document.body.appendChild(label);
    setTimeout(function() { if (label.parentNode) label.parentNode.removeChild(label); }, 950);

    // Floating hearts
    var offsets = [-40, -15, 10, 35, 55];
    var rotations = ['-15deg', '5deg', '-8deg', '12deg', '-3deg'];
    offsets.forEach(function(offsetX, i) {
      var heart = document.createElement('div');
      heart.className = 'ps-like-heart';
      heart.textContent = '♥';
      heart.style.left = (centerX + offsetX + (Math.random() * 20 - 10)) + 'px';
      heart.style.top = (centerY + (Math.random() * 40 - 20)) + 'px';
      heart.style.setProperty('--rot', rotations[i]);
      heart.style.setProperty('--dur', (0.65 + Math.random() * 0.3) + 's');
      heart.style.animationDelay = (i * 55) + 'ms';
      heart.style.fontSize = (18 + Math.random() * 12) + 'px';
      document.body.appendChild(heart);
      setTimeout(function() { if (heart.parentNode) heart.parentNode.removeChild(heart); }, 1100);
    });
  }

  /**
   * Builds the results product grid with staggered entry animations
   * @param {Array} products - AJAX API product objects
   */
  var BAG_ICON = '<svg width="14" height="20" viewBox="0 0 14 20" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="-0.5" y="0.5" width="12.8397" height="12.8397" transform="matrix(-1 0 0 1 12.8398 5.28947)" stroke="currentColor"/><path d="M3.10231 4.82723C3.10231 2.71349 4.81584 0.999969 6.92958 0.999969C9.04331 0.999969 10.7568 2.71349 10.7568 4.82723" stroke="currentColor"/><path d="M10.7568 4.56616V7.62797M3.10231 4.56616V7.62797" stroke="currentColor"/></svg>';

  function buildResultsGrid(products) {
    if (!els.resultsGrid) return;
    els.resultsGrid.innerHTML = products.map(function(product, i) {
      var image = product.images && product.images[0];
      var imageUrl = image ? resizeImage(image.src, '600x840') : '';
      var price = productPrice(product);
      var delay = (i * 110 + 200);
      var productUrl = '/products/' + escapeAttr(product.handle);
      var tags = productTags(product);
      var isLandscape = tags.indexOf('landskab') !== -1;
      var figureClass = isLandscape ? 'media portrait_alt contain' : 'media portrait_alt';
      var imgStyle = isLandscape ? ' style="object-fit:contain"' : '';
      return [
        '<div class="card card--product card--hq card--traditional col-span ps-result-animate" style="--ps-delay:' + delay + 'ms; grid-column:auto/span 1; grid-row:span 1;">',
        '  <a href="' + productUrl + '" class="card-media card-media--portrait p-0">',
        '    <figure class="' + figureClass + '">',
        imageUrl ? '      <img src="' + imageUrl + '" alt="' + escapeAttr(product.title) + '" loading="lazy"' + imgStyle + '>' : '',
        '    </figure>',
        '  </a>',
        '  <div class="card-media-overlay flex vertical-top horizontal-left"></div>',
        '  <div class="card-body text-align-left">',
        '    <p class="card-title">',
        '      <a href="' + productUrl + '">' + escapeHtml(product.title) + ' <span class="card-vendor">' + STR.vendorBy + ' ' + escapeHtml(product.vendor) + '</span></a>',
        '      <quick-buy data-product="' + product.id + '" data-url="' + productUrl + '" style="position:relative;opacity:1;float:right;text-align:right;bottom:0">',
        '        <a data-product-handle="' + escapeAttr(product.handle) + '" href="#modal-' + product.id + '-product">' + BAG_ICON + '</a>',
        '      </quick-buy>',
        '    </p>',
        '    <div class="price size--small mt-8"><span class="price-item price-item--regular">' + STR.pricePrefix + ' ' + price + '</span></div>',
        '  </div>',
        '</div>'
      ].join('');
    }).join('');
  }

  /**
   * Fetches specific products by handle via AJAX API for shared result links
   * @param {string[]} handles
   */
  function loadResultsFromHandles(handles) {
    showLoading(true);

    var promises = handles.map(function(handle) {
      var safe = handle.replace(/[^a-z0-9-]/g, '');
      return fetch('/products/' + safe + '.json')
        .then(function(res) { return res.json(); })
        .then(function(data) { return data && data.product; });
    });

    Promise.all(promises)
      .then(function(products) {
        products = products.filter(Boolean);
        showLoading(false);
        if (products.length > 0) {
          // A shared ?likes= link is still a swiper-driven visit — attribute it too.
          if (window.PosterSwiperAttribution) {
            window.PosterSwiperAttribution.save({
              sessionId: analytics.sessionId || newSessionId(),
              handles:   products.map(function(p) { return p.handle; }),
              vendors:   products.map(function(p) { return p.vendor; }),
              source:    'shared_link'
            });
          }
          buildResultsGrid(products);
          els.results.style.display = 'block';
        } else {
          fetchBatch();
        }
      })
      .catch(function() {
        showLoading(false);
        fetchBatch();
      });
  }

  function updateUrl(handles) {
    var url = new URL(window.location.href);
    url.searchParams.set('likes', handles.join(','));
    window.history.replaceState({}, '', url.toString());
  }

  function copyLink() {
    var url = window.location.href;
    if (!navigator.clipboard) return;
    navigator.clipboard.writeText(url).then(function() {
      var btn = document.getElementById('ps-copy-btn');
      if (!btn) return;
      var orig = btn.textContent;
      btn.textContent = 'Kopieret!';
      setTimeout(function() { btn.textContent = orig; }, 2000);
    });
  }

  function restart() {
    track('poster_swiper_restart');
    analytics.totalSwipes = 0;
    analytics.considerationTotalMs = 0;
    analytics.sessionStart = Date.now();
    analytics.sessionId = newSessionId();
    state.products = [];
    state.currentIndex = 0;
    state.likes = [];
    state.history = [];
    state.page = 1;
    state.hasNextPage = true;
    state.isFetching = false;
    updateUndoBtn();

    // Reset hearts
    if (els.hearts) {
      els.hearts.querySelectorAll('.poster-swiper__heart').forEach(function(h) {
        h.classList.remove('is-filled');
      });
    }

    var url = new URL(window.location.href);
    url.searchParams.delete('likes');
    window.history.replaceState({}, '', url.toString());

    els.results.style.display = 'none';
    els.swiper.style.display = '';
    els.swiper.classList.add('is-active');
    fetchBatch();
  }

  function showLoading(show) {
    if (els.loading) els.loading.style.display = show ? 'flex' : 'none';
  }

  /**
   * Formats a Shopify AJAX API price (integer cents) to Danish string
   * @param {number} cents
   */
  function formatMoney(cents) {
    if (cents == null || cents === '') return '';
    cents = parseInt(cents, 10);
    if (isNaN(cents)) return '';
    var value = cents / 100;
    var fmt = MONEY_FORMAT;
    fmt = fmt.replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, Math.round(value).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ','));
    fmt = fmt.replace(/\{\{\s*amount_no_decimals_with_space_separator\s*\}\}/g, Math.round(value).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '\u00a0'));
    fmt = fmt.replace(/\{\{\s*amount_no_decimals\s*\}\}/g, Math.round(value).toString());
    fmt = fmt.replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, value.toFixed(2).replace('.', ',').replace(/,00$/, ''));
    fmt = fmt.replace(/\{\{\s*amount_with_apostrophe_separator\s*\}\}/g, value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, "'").replace(/\.00$/, ''));
    fmt = fmt.replace(/\{\{\s*amount\s*\}\}/g, value.toFixed(2).replace(/\.00$/, ''));
    return fmt;
  }

  /** Price in integer cents, across every field shape the two product APIs return. */
  function priceCents(product) {
    if (product.price_min != null && product.price_min !== '') return product.price_min;
    if (product.price != null && product.price !== '') return product.price;
    if (product.variants && product.variants[0] && product.variants[0].price != null) {
      // product.json returns variant price as decimal string e.g. "299.00"
      return Math.round(parseFloat(product.variants[0].price) * 100);
    }
    return null;
  }

  /** Price as a decimal number for GA4's `value` parameter, or null when unknown. */
  function priceAmount(product) {
    var cents = parseInt(priceCents(product), 10);
    return isNaN(cents) ? null : Math.round(cents) / 100;
  }

  function currencyCode() {
    return (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || null;
  }

  function productPrice(product) {
    var cents = priceCents(product);
    if (ENABLE_LOGS) console.log('[swiper] price cents:', cents, 'format:', MONEY_FORMAT);
    return formatMoney(cents);
  }

  function formatAjaxPrice(cents) {
    return formatMoney(cents);
  }

  /**
   * Resizes a Shopify CDN image URL to the given size string (e.g. '600x840')
   * @param {string} src
   * @param {string} size
   */
  function resizeImage(src, size) {
    if (!src) return '';
    return src.replace(/(\.[^.?]+)(\?.*)?$/, '_' + size + '$1$2');
  }

  function shuffleArray(arr) {
    for (var i = arr.length - 1; i > 0; i--) {
      var j = Math.floor(Math.random() * (i + 1));
      var tmp = arr[i];
      arr[i] = arr[j];
      arr[j] = tmp;
    }
  }

  // Shopify collection API returns tags as array; product.json returns them as a string
  function productTags(product) {
    var t = product.tags || [];
    if (typeof t === 'string') t = t.split(',');
    return t.map(function(s) { return s.trim().toLowerCase(); });
  }

  function escapeHtml(str) {
    return String(str || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  function escapeAttr(str) {
    return String(str || '').replace(/"/g, '&quot;').replace(/&/g, '&amp;');
  }

  document.addEventListener('DOMContentLoaded', init);
})();

// Poster Swiper END




//mute button on product page prdocut image slider

document.addEventListener('DOMContentLoaded', () => {
  const video = document.getElementById("myProductSliderVideo");
  const btn = document.getElementById("productSliderVideoMuteBtn");
  if (!video || !btn) return; // only product pages with a slider video have the button

  btn.addEventListener("click", () => {
    video.muted = !video.muted;
    
    btn.innerHTML = video.muted ? `
<svg width="512" height="512" viewBox="0 0 512 512" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M277.046 328.303C267.247 328.303 259.303 336.247 259.303 346.046V408.693L138.746 313.261C133.221 308.902 126.392 306.526 119.354 306.515H61.6348V205.385H119.351C129.15 205.385 137.094 197.441 137.094 187.642C137.094 177.843 129.15 169.899 119.351 169.899H57.3956C40.1459 169.919 26.1675 183.898 26.1475 201.147V310.755C26.1675 328.004 40.1459 341.983 57.3956 342.003H117.876L250.999 447.379C262.702 456.64 279.697 454.66 288.958 442.958C292.735 438.185 294.79 432.276 294.79 426.189V346.047C294.79 336.248 286.846 328.304 277.047 328.304L277.046 328.303ZM506.706 481.613L430.918 405.827C508.655 314.314 503.137 178.457 418.234 93.5498C411.275 86.6521 400.04 86.7021 393.142 93.6618C386.287 100.578 386.287 111.726 393.143 118.642C464.61 190.109 468.853 304.107 405.716 380.619L364.064 338.967C405.201 284.613 399.945 208.244 351.748 160.037C344.788 153.139 333.553 153.189 326.656 160.15C319.802 167.066 319.802 178.214 326.656 185.13C361.418 219.893 365.431 274.223 338.619 313.521L294.793 269.693V85.7103C294.795 70.7871 282.698 58.6874 267.775 58.6854C261.686 58.6843 255.775 60.7401 251.001 64.5193L160.919 135.826L30.2888 5.19652C23.3601 -1.73217 12.1252 -1.73217 5.19652 5.19652C-1.73217 12.1252 -1.73217 23.3601 5.19652 30.2888L264.495 289.589L264.501 289.595L481.613 506.706C488.488 513.688 499.722 513.776 506.704 506.901C513.687 500.027 513.775 488.793 506.9 481.81L506.706 481.613ZM259.302 103.208V234.21L186.181 161.089L259.302 103.208Z" fill="black"/>
</svg>
` : `
<svg width="470" height="384" viewBox="0 0 470 384" fill="none" xmlns="http://www.w3.org/2000/svg">
<path fill-rule="evenodd" clip-rule="evenodd" d="M225.131 1.35346C227.967 2.6063 230.379 4.65651 232.072 7.25469C233.765 9.85287 234.666 12.887 234.667 15.9881V367.988C234.669 371.09 233.769 374.125 232.077 376.725C230.386 379.325 227.975 381.377 225.138 382.632C222.301 383.886 219.161 384.289 216.099 383.791C213.038 383.294 210.187 381.917 207.893 379.828L95.1467 277.321H37.3333C27.4319 277.321 17.936 273.388 10.9347 266.387C3.93332 259.385 0 249.89 0 239.988V143.988C0 123.38 16.7253 106.655 37.3333 106.655H95.1467L207.893 4.14813C210.189 2.066 213.04 0.695494 216.1 0.203161C219.16 -0.289172 222.298 0.117863 225.131 1.3748V1.35346ZM202.667 52.1481L112.107 134.495C109.159 137.174 105.317 138.658 101.333 138.655H37.3333C35.9188 138.655 34.5623 139.217 33.5621 140.217C32.5619 141.217 32 142.574 32 143.988V239.988C32 242.932 34.3893 245.321 37.3333 245.321H101.333C105.31 245.324 109.143 246.807 112.085 249.481L202.667 331.828V52.1481Z" fill="black"/>
<path d="M377.984 26.0575C380.984 23.0612 385.051 21.3782 389.291 21.3782C393.531 21.3782 397.597 23.0612 400.597 26.0575C492.245 117.705 492.245 266.271 400.597 357.919C397.564 360.745 393.553 362.284 389.408 362.211C385.262 362.137 381.308 360.458 378.376 357.527C375.445 354.595 373.765 350.64 373.692 346.495C373.619 342.35 375.158 338.339 377.984 335.305C396.806 316.486 411.737 294.143 421.924 269.552C432.111 244.962 437.354 218.605 437.354 191.988C437.354 165.371 432.111 139.015 421.924 114.424C411.737 89.8336 396.806 67.4905 377.984 48.6708C374.988 45.6708 373.305 41.6041 373.305 37.3641C373.305 33.1241 374.988 29.0575 377.984 26.0575Z" fill="black"/>
<path d="M325.184 101.471C323.698 99.9846 321.933 98.8057 319.992 98.0013C318.05 97.197 315.968 96.783 313.867 96.783C311.765 96.783 309.684 97.197 307.742 98.0013C305.8 98.8057 304.036 99.9846 302.549 101.471C301.063 102.957 299.884 104.721 299.08 106.663C298.276 108.605 297.862 110.686 297.862 112.788C297.862 114.89 298.276 116.971 299.08 118.913C299.884 120.855 301.063 122.619 302.549 124.105C311.464 133.02 318.535 143.603 323.36 155.25C328.184 166.898 330.667 179.381 330.667 191.988C330.667 204.595 328.184 217.079 323.36 228.726C318.535 240.373 311.464 250.956 302.549 259.871C299.723 262.904 298.184 266.916 298.258 271.061C298.331 275.206 300.01 279.161 302.941 282.092C305.873 285.024 309.828 286.703 313.973 286.776C318.118 286.849 322.13 285.31 325.163 282.484C349.166 258.48 362.65 225.924 362.65 191.977C362.65 158.031 349.187 125.475 325.184 101.471Z" fill="black"/>
</svg>
`;
  });
});



// Wishlist King hearts on product cards: one heart per card, pinned top-right, always visible.
// Why cards collected duplicates: the app's collection-button selector
// (.card-media[href*="/products/"], set in the Wishlist King admin) matches EVERY slide link in
// a card (2 mobile + 10 desktop slides), and the app lazily inserts one
// <wishlist-button-collection> after each link as it scrolls into view, plus one in the title.
// That is up to 13 lit components per card, each with its own wishlist subscription and
// floating-ui positioning. The old fix hid the extras and re-scanned the whole document on
// every DOM mutation and class change on the page.
// Now: the first button per card is moved to the card and marked; every other one is removed
// (the app does not re-inject for a link it has already handled). Work only happens when a
// wishlist button was actually added, batched to one pass per frame.
(function () {
  var MARK = 'data-wk-card-heart';

  function claim(el) {
    if (el.hasAttribute(MARK) || el.classList.contains('product-page-wishlist-btn')) return;
    var card = el.closest('.card');
    if (!card) return;
    // The app injects exactly one heart per card, after the title link (Swish custom
    // collection-buttons.js; the embed's default per-image-link injection is switched off in
    // settings_data, product_link_selector). Keep that one and move it to the corner; remove extras.
    if (card.hasAttribute(MARK)) {
      el.remove();
      return;
    }
    card.setAttribute(MARK, '');
    el.setAttribute(MARK, '');
    // Direct child of .card so overflow:hidden on card-media / the slider doesn't clip it.
    if (el.parentElement !== card) card.appendChild(el);
    el.style.setProperty('display', 'block', 'important');
    el.style.setProperty('opacity', '1', 'important');
    el.style.setProperty('visibility', 'visible', 'important');
    el.style.setProperty('position', 'absolute', 'important');
    // Position comes from custom.css (--card-heart-top), 20px = top-right as before.
    el.style.setProperty('top', 'var(--card-heart-top, 20px)', 'important');
    el.style.setProperty('right', '10px', 'important');
    el.style.setProperty('z-index', '10', 'important');
    el.style.removeProperty('transform');
  }

  function run() {
    document.querySelectorAll('wishlist-button-collection:not([' + MARK + '])').forEach(claim);
  }

  var queued = false;
  function schedule() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(function () {
      queued = false;
      run();
    });
  }

  document.addEventListener('wk:ready', schedule);
  setTimeout(run, 500);
  setTimeout(run, 1500);

  // The app inserts each <wishlist-button-collection> as its own node, so checking the added
  // nodes' names is enough: no DOM queries per mutation, no attribute watching.
  new MutationObserver(function (records) {
    for (var i = 0; i < records.length; i++) {
      var added = records[i].addedNodes;
      for (var j = 0; j < added.length; j++) {
        if (added[j].nodeName === 'WISHLIST-BUTTON-COLLECTION') return schedule();
      }
    }
  }).observe(document.body, { childList: true, subtree: true });
})();

// Collection view toolbar (grid + product/lifestyle)
(function () {
  if (!document.getElementById('product-grid-ajax')) return;

  function getGrid() {
    return document.getElementById('product-grid-ajax');
  }

  function currentSlideIndex() {
    return (localStorage.getItem('collection-image-view') || 'product') === 'lifestyle' ? 1 : 0;
  }

  // Put each card slider on the slide for the current image view (1 = lifestyle).
  // JUMP, never animate: the slider's own scrollToSlide() is a smooth scroll, and
  // dozens of those at once ("Vis flere", restore, toggle) get cut short by page
  // scrolling and layout, leaving tracks stuck between two slides (half product,
  // half lifestyle image, badges and heart shifted out of the card).
  // Sliders that are not laid out (display:none: a held-back row, the other
  // breakpoint's slider) are skipped and picked up by syncSliders() later.
  // onlyPending: leave sliders that already show this view alone (a visitor's swipe).
  function scrollSliders(container, slideIndex, onlyPending) {
    container.querySelectorAll('.product-slider--desktop, .product-slider--mobile').forEach(function (slider) {
      if (onlyPending && slider.dataset.viewSlide === String(slideIndex)) return;
      var track = slider.querySelector('.gridy-track');
      var slide = track && track.children[slideIndex];
      if (!slide || !track.clientWidth) return;
      track.style.scrollBehavior = 'auto';
      track.scrollLeft = slide.offsetLeft - track.children[0].offsetLeft;
      track.style.scrollBehavior = '';
      slider.dataset.viewSlide = String(slideIndex);
      if (slider.config) slider.config.current_slide = slideIndex;
    });
  }

  var syncQueued = false;
  function syncSliders() {
    if (syncQueued) return;
    syncQueued = true;
    requestAnimationFrame(function () {
      syncQueued = false;
      var grid = getGrid();
      if (grid) scrollSliders(grid, currentSlideIndex(), true);
    });
  }

  function setGridView(view) {
    var grid = getGrid();
    if (!grid) return;
    grid.classList.toggle('view--single', view === 'single');
    document.querySelectorAll('.collection-grid-btn').forEach(function (btn) {
      btn.classList.toggle('active', btn.dataset.grid === view);
    });
    localStorage.setItem('collection-grid-view', view);
    // Re-apply image view after layout change so sliders land on the right slide
    var slideIndex = currentSlideIndex();
    if (slideIndex > 0) {
      setTimeout(function () { scrollSliders(grid, slideIndex); }, 50);
    }
  }

  function setImageView(view) {
    var grid = getGrid();
    if (!grid) return;
    grid.classList.toggle('view--lifestyle', view === 'lifestyle');
    document.querySelectorAll('.collection-image-btn').forEach(function (btn) {
      btn.classList.toggle('active', btn.dataset.image === view);
    });
    scrollSliders(grid, view === 'lifestyle' ? 1 : 0);
    localStorage.setItem('collection-image-view', view);
  }

  // Apply the current view. The merchant "Default view" setting is authoritative: when
  // it changes, every visitor is reset to the new default. A visitor's own toggle is
  // only preserved while the configured default is unchanged (tracked separately so a
  // stale localStorage value can never override a freshly-changed setting).
  function applyState() {
    var grid = getGrid();
    if (!grid) return;
    var defaultImageView = grid.dataset.defaultImageView === 'lifestyle' ? 'lifestyle' : 'product';
    var appliedDefault = localStorage.getItem('collection-image-view-default');
    var imageView;
    if (appliedDefault !== defaultImageView) {
      // Setting is new or was changed by the merchant → apply it, drop any prior choice.
      imageView = defaultImageView;
      localStorage.setItem('collection-image-view-default', defaultImageView);
      localStorage.setItem('collection-image-view', imageView);
    } else {
      imageView = localStorage.getItem('collection-image-view') || defaultImageView;
    }
    setGridView(localStorage.getItem('collection-grid-view') || 'default');
    setImageView(imageView);
  }

  applyState();

  // Re-apply when the theme editor re-renders the section, so setting changes preview live.
  document.addEventListener('shopify:section:load', applyState);

  document.addEventListener('click', function (e) {
    var gridBtn = e.target.closest('.collection-grid-btn');
    if (gridBtn) setGridView(gridBtn.dataset.grid);

    var imageBtn = e.target.closest('.collection-image-btn');
    if (imageBtn) setImageView(imageBtn.dataset.image);
  });

  // When infinite scroll adds new cards, apply current view and eager-load images
  var observedGrid = getGrid();
  if (observedGrid) {
    new MutationObserver(function (mutations) {
      var sync = false;
      mutations.forEach(function (mutation) {
        // A held-back row being shown again (load-more-deferred removed).
        if (mutation.type === 'attributes') { sync = true; return; }
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          // Eager-load only what the view toggle can show without a swipe: the first
          // two slides (product + lifestyle) of each card slider. Eager-loading every
          // slide of both breakpoint sliders queued ~10 images per card (~360 per
          // filter/"Vis flere" batch), which starved the next filter request.
          // Later slides stay lazy and load when swiped into view.
          node.querySelectorAll('img[loading="lazy"]').forEach(function (img) {
            var track = img.closest('.gridy-track');
            if (track) {
              var slide = img.closest('.gridy-track > *');
              if (!slide || Array.prototype.indexOf.call(track.children, slide) > 1) return;
            }
            img.setAttribute('loading', 'eager');
          });
          if (node.querySelector('.gridy-track')) sync = true;
        });
      });
      if (sync) syncSliders();
    }).observe(observedGrid, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
  }
  // Crossing the tablet breakpoint swaps the mobile and desktop sliders; the newly
  // shown one has not been placed yet.
  window.addEventListener('resize', syncSliders, { passive: true });
})();

// Sticky filter bar: measure total fixed top height and expose as CSS variable
(function () {
  function updateStickyTop() {
    var total = 0;
    // Elements at top: 0 (announcement bars, discount banner)
    document.querySelectorAll('body > *, [data-section-type], .site-header, .discount-topbanner').forEach(function (el) {
      var style = window.getComputedStyle(el);
      var rect = el.getBoundingClientRect();
      if ((style.position === 'fixed' || style.position === 'sticky') && rect.top >= 0 && rect.top < 10) {
        total = Math.max(total, rect.bottom);
      }
    });
    // Always include site-header bottom (it can be sticky at top: 35px due to announcement bar CSS)
    var siteHeader = document.querySelector('.site-header');
    if (siteHeader) {
      var sh = window.getComputedStyle(siteHeader);
      if (sh.position === 'fixed' || sh.position === 'sticky') {
        total = Math.max(total, siteHeader.getBoundingClientRect().bottom);
      }
    }
    document.documentElement.style.setProperty('--total-sticky-top', total + 'px');
  }
  window.addEventListener('scroll', updateStickyTop, { passive: true });
  window.addEventListener('resize', updateStickyTop);
  document.addEventListener('DOMContentLoaded', updateStickyTop);
  setTimeout(updateStickyTop, 500);
})();

(function () {
  function initSliderInView(slider) {
    var track = slider.querySelector('.gridy-track');
    if (!track) return;
    var slides = Array.from(track.children);
    if (!slides.length) return;

    function updateInView() {
      var trackRect = track.getBoundingClientRect();
      var best = -1, bestOverlap = -1;
      slides.forEach(function (slide, i) {
        var r = slide.getBoundingClientRect();
        var overlap = Math.min(r.right, trackRect.right) - Math.max(r.left, trackRect.left);
        if (overlap > bestOverlap) { bestOverlap = overlap; best = i; }
      });
      slides.forEach(function (slide, i) {
        slide.classList.toggle('in-view', i === best);
      });
    }

    track.addEventListener('scroll', updateInView, { passive: true });
    updateInView();
  }

  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('.product-media-slider').forEach(initSliderInView);
  });
})();

(function () {
  function initProductVideoPlayers() {
    document.querySelectorAll('.product-video-player').forEach(function (video) {
      var btn = video.parentElement.querySelector('.product-video-play-btn');
      if (!btn) return;

      btn.addEventListener('click', function () {
        video.play();
      });

      video.addEventListener('click', function () {
        if (video.paused) {
          video.play();
        } else {
          video.pause();
        }
      });

      video.addEventListener('play', function () {
        btn.classList.add('is-playing');
      });

      video.addEventListener('pause', function () {
        btn.classList.remove('is-playing');
      });

      video.addEventListener('ended', function () {
        btn.classList.remove('is-playing');
      });
    });
  }

  document.addEventListener('DOMContentLoaded', initProductVideoPlayers);
})();

/* Hover arrows for featured-collection product sliders (incl. lazily injected recommendations). */
  (function () {
    if (window.__fcpArrows) return; window.__fcpArrows = true;
    function sync(wrap) {
      var track = wrap.querySelector('.fcp--slider-main'); if (!track) return;
      var max = track.scrollWidth - track.clientWidth - 2;
      var prev = wrap.querySelector('[data-fcp-arrow="-1"]'), next = wrap.querySelector('[data-fcp-arrow="1"]');
      if (prev) prev.hidden = track.scrollLeft <= 2;
      if (next) next.hidden = max <= 0 || track.scrollLeft >= max;
    }
    function init() {
      document.querySelectorAll('.fcp-wrap').forEach(function (wrap) {
        var track = wrap.querySelector('.fcp--slider-main'); if (!track || wrap.__fcp) return; wrap.__fcp = true;
        track.addEventListener('scroll', function () { sync(wrap); }, { passive: true });
        sync(wrap);
      });
    }
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-fcp-arrow]'); if (!btn) return;
      var wrap = btn.closest('.fcp-wrap'); var track = wrap && wrap.querySelector('.fcp--slider-main'); if (!track) return;
      track.scrollBy({ left: Number(btn.dataset.fcpArrow) * track.clientWidth * 0.8, behavior: 'smooth' });
    });
    if (document.readyState !== 'loading') init(); else document.addEventListener('DOMContentLoaded', init);
    // Sliders injected later (e.g. product recommendations) are set up on first hover/scroll.
    document.addEventListener('mouseover', function (e) { var w = e.target.closest && e.target.closest('.fcp-wrap'); if (w && !w.__fcp) { init(); } }, { passive: true });
    new MutationObserver(function () { init(); }).observe(document.documentElement, { childList: true, subtree: true });
    window.addEventListener('load', function(){ document.querySelectorAll('.fcp-wrap').forEach(sync); });
    window.addEventListener('resize', function(){ document.querySelectorAll('.fcp-wrap').forEach(sync); });
  })();
